import React, { useState, useEffect, useRef } from 'react';
import DebouncedSearchInput from '../../components/ui/debounced-search-input';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatChile } from '../../utils/dateUtils';
import {
  Plus, Eye, Calendar, TrendingUp,
  Edit2, Trash2, Loader2, Crown,
  CreditCard, Camera, User,
  Globe, Phone, CheckCircle, ChevronLeft, ChevronRight,
  Mail, Inbox, CheckCheck, ExternalLink, MapPin, Star, Ticket, GalleryHorizontal,
  Clock, ToggleLeft, ToggleRight, Building2, Copy, Rocket, Video, Zap, Search, Landmark, FileSpreadsheet, Download, Upload, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { Footer } from '../../components/Footer';
import { MembershipTab } from '../../components/provider/MembershipTab';
import { OnboardingTab } from '../../components/provider/OnboardingTab';
import { EventTicketsDialog } from '../../components/provider/EventTicketsDialog';

import { Switch } from '../../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
};

// Helper to resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // If it's a relative URL from our API, prepend the API_URL
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  // Otherwise, return as is
  return url;
};

// Safely extract error message from axios error (FastAPI may return string OR array of validation objects)
const getErrorMessage = (error, fallback = 'Por favor intenta de nuevo.') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(d => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d))))
      .join('; ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return fallback;
};

import { LocationPickerModal } from '../../components/LocationPickerModal';
import { VenueAutocomplete } from '../../components/VenueAutocomplete';
import { OrganizerAutocomplete } from '../../components/OrganizerAutocomplete';
import { DEFAULT_EVENT_IMAGE } from '../../utils/imageUrl';

// Event Form Component - extracted to prevent re-renders
// Thumbnail with graceful fallback. Instagram/Facebook CDN URLs rotate their
// signature and eventually return HTML instead of an image — this component
// swaps to a neutral placeholder + exposes `data-broken="1"` so the row can
// render an "Imagen caída" badge and admins spot which events need a fresh
// poster. Runs client-side, no extra requests.
const EventThumb = ({ src, alt, eventId }) => {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [src]);
  const showFallback = !src || broken;
  return (
    <div
      className="w-14 h-14 md:w-16 md:h-16 rounded-xl shrink-0 overflow-hidden bg-gray-50"
      data-broken={broken ? '1' : undefined}
      data-testid={`provider-event-thumb-${eventId}`}
    >
      <img
        src={showFallback ? DEFAULT_EVENT_IMAGE : src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={() => { if (!broken) setBroken(true); }}
      />
    </div>
  );
};

const EventForm = React.memo(({
  event, 
  setEvent, 
  onSubmit, 
  isEdit, 
  categories, 
  communes, 
  regions = [],
  venues,
  submitting,
  onCancel,
  t,
  isAdmin = false,
  adminProviders = [],
  bulkDrafts = [],
  bulkErrors = [],
  onBulkFile = null,
  onDownloadTemplate = null,
  onLoadDraft = null,
  onDismissDraft = null,
  onPublishAllDrafts = null,
  bulkParsing = false,
  bulkPublishingAll = false,
  bulkPreflightLoading = false,
  bulkProgress = null
}) => {
  // Use local state for form fields to prevent parent re-renders
  const [localEvent, setLocalEvent] = React.useState(event);
  const [imageMode, setImageMode] = React.useState('url');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [mapModalOpen, setMapModalOpen] = React.useState(false);
  // Index of the tour stop whose map picker is open; null = closed.
  const [stopMapOpenIdx, setStopMapOpenIdx] = React.useState(null);
  const eventImageRef = React.useRef(null);

  // Region selector (drives the commune dropdown). Derived from the picked commune
  // in edit mode so the region shown matches the persisted commune_id.
  const communeRegionId = React.useMemo(() => {
    if (!event.commune_id) return '';
    const c = communes.find((x) => x.id === event.commune_id);
    return c?.region_id || '';
  }, [event.commune_id, communes]);
  const [selectedRegionId, setSelectedRegionId] = React.useState(communeRegionId);
  React.useEffect(() => { setSelectedRegionId(communeRegionId); }, [communeRegionId]);
  const filteredCommunes = React.useMemo(() => {
    if (!selectedRegionId) return communes;
    return communes.filter((c) => c.region_id === selectedRegionId);
  }, [communes, selectedRegionId]);
  
  // Operating hours state
  const defaultHours = {};
  DAY_KEYS.forEach(d => { defaultHours[d] = null; });
  const [operatingHours, setOperatingHours] = React.useState(
    event.operating_hours || defaultHours
  );
  
  // Sync local state when event prop changes (for edit mode)
  React.useEffect(() => {
    setLocalEvent(event);
    if (event.operating_hours) {
      setOperatingHours(event.operating_hours);
    }
  }, [event]);

  const handleFieldChange = (field, value) => {
    setLocalEvent(prev => ({ ...prev, [field]: value }));
    if (field === 'main_image' && value) setImageError(false);
  };
  
  const handleDayToggle = (day) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: prev[day] ? null : { open: '09:00', close: '18:00' }
    }));
  };
  
  const handleHourChange = (day, field, value) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleAllDayToggle = (day) => {
    setOperatingHours(prev => {
      const current = prev[day];
      if (!current) return prev;
      const isAllDay = current.all_day;
      return {
        ...prev,
        [day]: isAllDay
          ? { open: '09:00', close: '18:00' }
          : { open: '00:00', close: '23:59', all_day: true }
      };
    });
  };

  // Recurring "dates" mode handlers
  const recurringDates = Array.isArray(localEvent.recurring_dates) ? localEvent.recurring_dates : [];
  const addRecurringDate = () => {
    const next = [...recurringDates, { date: '', open: '20:00', close: '22:00', all_day: false }];
    handleFieldChange('recurring_dates', next);
  };
  const updateRecurringDate = (idx, patch) => {
    const next = recurringDates.map((d, i) => i === idx ? { ...d, ...patch } : d);
    handleFieldChange('recurring_dates', next);
  };
  const removeRecurringDate = (idx) => {
    handleFieldChange('recurring_dates', recurringDates.filter((_, i) => i !== idx));
  };

  // Downscale + re-encode any image on the client so uploads are small and the CDN
  // returns something the browser can paint in a single frame (no more top-to-bottom fade).
  const compressImage = (file, maxDim = 1600, quality = 0.82) => new Promise((resolve, reject) => {
    // GIFs / very small files: pass through unchanged (would lose animation / gain nothing).
    if (file.type === 'image/gif' || file.size < 300 * 1024) return resolve(file);
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read-error'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(file); // fallback: use original
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width >= height) { height = Math.round(height * (maxDim / width)); width = maxDim; }
            else { width = Math.round(width * (maxDim / height)); height = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) return resolve(file);
              // Only use the compressed version if it actually shrunk the payload.
              if (blob.size >= file.size) return resolve(file);
              resolve(new File([blob], (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }));
            },
            'image/jpeg',
            quality,
          );
        } catch { resolve(file); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handleImageUpload = async (e, slot = 'main') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Allow slightly bigger originals now — we compress before upload.
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Tamaño máximo: 10MB');
      return;
    }

    // Instant local preview so the user sees the picked image with no network round-trip.
    const previewUrl = URL.createObjectURL(file);
    setLocalPreviews(prev => {
      const old = prev[slot];
      if (old) { try { URL.revokeObjectURL(old); } catch { /* noop */ } }
      return { ...prev, [slot]: previewUrl };
    });

    setUploadingImage(true);
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('file', compressed, compressed.name || 'photo.jpg');
      const res = await axios.post(`${API_URL}/api/uploads/event-image`, formData);
      if (slot === 'main') {
        handleFieldChange('main_image', res.data.url);
      } else {
        const current = localEvent.additional_images || [];
        const idx = parseInt(slot);
        const updated = [...current];
        updated[idx] = { url: res.data.url, position: '50%' };
        handleFieldChange('additional_images', updated);
      }
      toast.success('Imagen subida correctamente');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir la imagen'));
      // Drop the failed preview so the user can retry cleanly.
      setLocalPreviews(prev => {
        const next = { ...prev };
        if (next[slot]) { try { URL.revokeObjectURL(next[slot]); } catch { /* noop */ } delete next[slot]; }
        return next;
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const additionalImageRefs = [React.useRef(null), React.useRef(null)];

  // Local blob URLs for immediate preview after picking a file (avoids re-fetching the
  // freshly-uploaded image from the CDN, which caused slow top-to-bottom rendering).
  // Keyed by slot: 'main' | '0' | '1'. Values are object URLs — revoked on unmount / replacement.
  const [localPreviews, setLocalPreviews] = React.useState({});
  React.useEffect(() => () => {
    // Revoke any lingering blob URLs when the form unmounts.
    Object.values(localPreviews).forEach(u => { try { URL.revokeObjectURL(u); } catch { /* noop */ } });
  }, []);

  const [imageError, setImageError] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate image is present
    if (!localEvent.main_image || !localEvent.main_image.trim()) {
      setImageError(true);
      toast.error('Debes agregar una imagen para el evento');
      return;
    }
    setImageError(false);

    // Validate scheduled_publish_at is strictly in the future (Chile local wall-clock).
    if (localEvent.scheduled_publish_at) {
      const pad = (n) => String(n).padStart(2, '0');
      const nowD = new Date();
      const nowLocal = `${nowD.getFullYear()}-${pad(nowD.getMonth() + 1)}-${pad(nowD.getDate())}T${pad(nowD.getHours())}:${pad(nowD.getMinutes())}`;
      if (localEvent.scheduled_publish_at <= nowLocal) {
        toast.error('La fecha de publicación debe ser posterior al momento actual');
        return;
      }
    }
    
    // Build final event data
    const finalData = { ...localEvent };
    if (localEvent.is_permanent || localEvent.is_recurring) {
      finalData.operating_hours = operatingHours;
      // Clear dates for permanent/recurring events
      finalData.start_date = null;
      finalData.end_date = null;
    }
    
    // Update parent state and call onSubmit with the local data
    setEvent(finalData);
    await onSubmit(finalData);
  };

  // Precomputed values for the "Programar publicación" section (used for both min= and inline validation).
  // datetime-local ignores UTC offsets, so we build a local wall-clock string.
  const _pad = (n) => String(n).padStart(2, '0');
  const _nowD = new Date();
  const nowLocal = `${_nowD.getFullYear()}-${_pad(_nowD.getMonth() + 1)}-${_pad(_nowD.getDate())}T${_pad(_nowD.getHours())}:${_pad(_nowD.getMinutes())}`;
  const schedulePast = !!(localEvent.scheduled_publish_at && localEvent.scheduled_publish_at <= nowLocal);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-4">
      {isAdmin && !isEdit && adminProviders.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-3 space-y-2 mb-4" data-testid="publish-as-block">
          <div className="flex items-center gap-2">
            <Landmark className="w-4 h-4 text-amber-700" />
            <Label className="text-sm font-semibold text-amber-900 m-0">Publicar como</Label>
          </div>
          <p className="text-[11px] text-amber-800/80">
            Como admin puedes publicar este evento en nombre de cualquier proveedor. Por defecto se usa PlanCity.
          </p>
          <Select
            value={localEvent.publish_as_provider_id || ''}
            onValueChange={(v) => handleFieldChange('publish_as_provider_id', v)}
          >
            <SelectTrigger data-testid="publish-as-provider-select" className="bg-white">
              <SelectValue placeholder="Seleccionar proveedor" />
            </SelectTrigger>
            <SelectContent>
              {adminProviders.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.business_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {isAdmin && !isEdit && (
        <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 p-3 space-y-3 mb-4" data-testid="bulk-upload-block">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <FileSpreadsheet className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <Label className="text-sm font-semibold text-emerald-900 m-0 block">Cargar desde Excel</Label>
                <p className="text-[11px] text-emerald-800/80 mt-0.5">
                  Sube la plantilla PlanCity para autocompletar el formulario. Reconoce las columnas aunque estén escritas diferente (ej: "Lugar del evento" = "Nombre del recinto").
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {onDownloadTemplate && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={onDownloadTemplate}
                  data-testid="bulk-download-template-btn"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Plantilla
                </Button>
              )}
              <label className="inline-flex">
                <input
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && onBulkFile) onBulkFile(f);
                    e.target.value = '';
                  }}
                  data-testid="bulk-file-input"
                  disabled={bulkParsing}
                />
                <span className={`inline-flex items-center gap-1 h-8 px-2 rounded-md border text-xs cursor-pointer ${bulkParsing ? 'bg-gray-100 text-gray-500 border-gray-200' : 'bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700'}`}>
                  {bulkParsing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  {bulkParsing ? 'Leyendo…' : 'Subir Excel'}
                </span>
              </label>
            </div>
          </div>
          {bulkDrafts.length > 0 && (
            <div className="space-y-1.5" data-testid="bulk-drafts-list">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-medium text-emerald-900">
                  {bulkDrafts.length} evento{bulkDrafts.length === 1 ? '' : 's'} detectado{bulkDrafts.length === 1 ? '' : 's'}. Edita uno o publica todos.
                </p>
                {onPublishAllDrafts && bulkDrafts.length > 1 && (
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
                    onClick={onPublishAllDrafts}
                    disabled={bulkPublishingAll || bulkPreflightLoading}
                    data-testid="bulk-publish-all-btn"
                  >
                    {bulkPreflightLoading ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Verificando duplicados…
                      </>
                    ) : bulkPublishingAll ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Publicando {bulkProgress?.current || 0}/{bulkProgress?.total || bulkDrafts.length}
                      </>
                    ) : (
                      <>
                        <Rocket className="w-3 h-3 mr-1" />
                        Publicar todos ({bulkDrafts.length})
                      </>
                    )}
                  </Button>
                )}
              </div>
              {bulkDrafts.some(d => d.warnings?.length > 0) && (
                <div className="rounded-md bg-blue-50 border border-blue-200 px-2 py-1 text-[11px] text-blue-800">
                  Los avisos <span aria-hidden>ℹ</span> no impiden publicar. El evento se creará igual, pero conviene reemplazar la imagen si aparece marcada.
                </div>
              )}
              <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
                {bulkDrafts.map((d) => (
                  <div
                    key={d.row}
                    className="flex items-center gap-2 rounded-md bg-white border border-emerald-200 px-2 py-1.5 text-xs hover:border-emerald-500 hover:bg-emerald-50 transition"
                  >
                    <button
                      type="button"
                      className="flex-1 text-left min-w-0"
                      onClick={() => onLoadDraft && onLoadDraft(d)}
                      data-testid={`bulk-draft-load-${d.row}`}
                    >
                      <span className="text-emerald-900 font-medium block truncate">{d.payload?.name || `Fila ${d.row}`}</span>
                      {d.warnings?.length > 0 && (
                        <span className="text-[10px] text-blue-700 block truncate">ℹ {d.warnings.join(' · ')}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                      onClick={() => onDismissDraft && onDismissDraft(d.row)}
                      data-testid={`bulk-draft-dismiss-${d.row}`}
                      title="Descartar"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {bulkErrors.length > 0 && (
            <div className="rounded-md bg-red-50 border border-red-200 p-2 space-y-0.5" data-testid="bulk-errors-list">
              <p className="text-[11px] font-medium text-red-800">
                {bulkErrors.length} fila{bulkErrors.length === 1 ? '' : 's'} con error (se omitieron):
              </p>
              <ul className="text-[11px] text-red-700 max-h-24 overflow-y-auto list-disc pl-4">
                {bulkErrors.map((e) => (
                  <li key={e.row}>Fila {e.row}: {e.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {!isEdit && (
        <div className="rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/40 p-3 flex items-center justify-between mb-4" data-testid="tour-mode-block">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎪</span>
            <div>
              <p className="text-sm font-semibold text-violet-900">Modo Gira</p>
              <p className="text-[11px] text-violet-700">Publica un show que pasa por varias ciudades. Se creará 1 evento por parada, cada uno en su comuna, con el mismo título y descripción.</p>
            </div>
          </div>
          <Switch
            checked={!!localEvent.is_tour}
            onCheckedChange={(v) => {
              handleFieldChange('is_tour', v);
              if (v && (!localEvent.tour_stops || localEvent.tour_stops.length === 0)) {
                handleFieldChange('tour_stops', [{ start_date: '', end_date: '', venue_name: '', address: '', commune_id: '', latitude: null, longitude: null, price: '' }]);
              }
            }}
            data-testid="event-tour-toggle"
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('eventName')} *</Label>
          <Input
            value={localEvent.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            required
            data-testid="event-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('category')} *</Label>
          <Select
            value={localEvent.category_id}
            onValueChange={(v) => handleFieldChange('category_id', v)}
          >
            <SelectTrigger data-testid="event-category-select">
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Categoría adicional <span className="text-xs text-gray-500 font-normal">(opcional)</span></Label>
        <Select
          value={localEvent.category_id_2 || '__none__'}
          onValueChange={(v) => handleFieldChange('category_id_2', v === '__none__' ? '' : v)}
        >
          <SelectTrigger data-testid="event-category-2-select">
            <SelectValue placeholder="Ninguna" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Ninguna —</SelectItem>
            {categories
              .filter(cat => cat.id !== localEvent.category_id)
              .map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-gray-500">Tu evento aparecerá también cuando se filtre por esta segunda categoría.</p>
      </div>

      <div className="space-y-2">
        <Label>{t('shortDescription')}</Label>
        <Input
          value={localEvent.short_description || ''}
          onChange={(e) => handleFieldChange('short_description', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label>{t('description')}</Label>
        <Textarea
          value={localEvent.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          rows={3}
        />
      </div>

      {/* Permanent Event Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-[#064E3B]/20 bg-[#064E3B]/5" data-testid="permanent-toggle-section">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-[#064E3B]" />
          <div>
            <p className="font-semibold text-[#064E3B]">Evento Permanente</p>
            <p className="text-xs text-gray-500">Pubs, restaurantes, sitios turísticos (sin fecha de término)</p>
          </div>
        </div>
        <Switch
          checked={localEvent.is_permanent || false}
          onCheckedChange={(checked) => {
            handleFieldChange('is_permanent', checked);
            if (checked) {
              handleFieldChange('is_recurring', false);
              handleFieldChange('is_online', false);
            }
          }}
          data-testid="permanent-toggle"
        />
      </div>

      {/* Recurring Event Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-orange-500/30 bg-orange-50" data-testid="recurring-toggle-section">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-orange-600" />
          <div>
            <p className="font-semibold text-orange-700">Evento Recurrente</p>
            <p className="text-xs text-gray-600">Se repite en días específicos. Puede ser semanal (ej. Karaoke todos los miércoles 21:00) o en fechas concretas (ej. Función teatral 22, 23 y 24 de mayo). No muestra "Cerrado" en otros días ni horarios.</p>
          </div>
        </div>
        <Switch
          checked={localEvent.is_recurring || false}
          onCheckedChange={(checked) => {
            handleFieldChange('is_recurring', checked);
            if (checked) {
              handleFieldChange('is_permanent', false);
              handleFieldChange('is_online', false);
            }
          }}
          data-testid="recurring-toggle"
        />
      </div>

      {/* Online Event Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-violet-500/30 bg-violet-50" data-testid="online-toggle-section">
        <div className="flex items-center gap-3">
          <Video className="w-5 h-5 text-violet-600" />
          <div>
            <p className="font-semibold text-violet-700">Evento Online</p>
            <p className="text-xs text-gray-600">Sin lugar físico. Se transmite por Zoom, Google Meet, Teams, YouTube, etc.</p>
          </div>
        </div>
        <Switch
          checked={localEvent.is_online || false}
          onCheckedChange={(checked) => {
            handleFieldChange('is_online', checked);
            if (checked) {
              handleFieldChange('is_permanent', false);
              handleFieldChange('is_recurring', false);
            }
          }}
          data-testid="online-toggle"
        />
      </div>

      {/* Online platform & URL fields */}
      {localEvent.is_online && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 rounded-xl bg-violet-50/50 border border-violet-200">
          <div className="space-y-1.5">
            <Label className="text-violet-700">Plataforma</Label>
            <Select
              value={localEvent.online_platform || ''}
              onValueChange={(v) => handleFieldChange('online_platform', v)}
            >
              <SelectTrigger data-testid="online-platform-select">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="zoom">Zoom</SelectItem>
                <SelectItem value="meet">Google Meet</SelectItem>
                <SelectItem value="teams">Microsoft Teams</SelectItem>
                <SelectItem value="youtube">YouTube Live</SelectItem>
                <SelectItem value="twitch">Twitch</SelectItem>
                <SelectItem value="instagram">Instagram Live</SelectItem>
                <SelectItem value="other">Otra</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-violet-700">Link para unirse *</Label>
            <Input
              type="url"
              placeholder="https://meet.google.com/xxx-yyyy-zzz"
              value={localEvent.online_url || ''}
              onChange={(e) => handleFieldChange('online_url', e.target.value)}
              data-testid="online-url-input"
              required={localEvent.is_online}
            />
          </div>
        </div>
      )}

      {/* Operating Hours - shown for permanent or recurring events */}
      {(localEvent.is_permanent || localEvent.is_recurring) && (
        <div className="space-y-4 p-4 rounded-xl bg-gray-50 border" data-testid="operating-hours-section">
          <Label className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[#064E3B]" />
            {localEvent.is_recurring ? 'Días y horarios del evento recurrente' : 'Horarios de Operación'}
          </Label>

          {/* Recurring mode selector (only for recurring events) */}
          {localEvent.is_recurring && (
            <div className="space-y-2 -mt-1" data-testid="recurring-mode-selector">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleFieldChange('recurring_mode', 'weekly')}
                  className={`text-xs px-3 py-1.5 rounded-full border-2 transition-colors ${
                    (localEvent.recurring_mode || 'weekly') === 'weekly'
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                  }`}
                  data-testid="recurring-mode-weekly"
                >
                  Semanal
                </button>
                <button
                  type="button"
                  onClick={() => handleFieldChange('recurring_mode', 'dates')}
                  className={`text-xs px-3 py-1.5 rounded-full border-2 transition-colors ${
                    localEvent.recurring_mode === 'dates'
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                  }`}
                  data-testid="recurring-mode-dates"
                >
                  Fechas específicas
                </button>
              </div>
              <p className="text-[11px] text-gray-500">
                {(localEvent.recurring_mode || 'weekly') === 'weekly'
                  ? 'Se repite cada semana en los días marcados.'
                  : 'Solo aparece en las fechas específicas que agregues.'}
              </p>
            </div>
          )}

          {/* Recurring end date (only for weekly mode) */}
          {localEvent.is_recurring && (localEvent.recurring_mode || 'weekly') === 'weekly' && (
            <div className="space-y-1.5 mb-2">
              <Label className="text-xs text-orange-700 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Fecha de término (opcional)
              </Label>
              <Input
                type="date"
                value={localEvent.recurring_end_date ? String(localEvent.recurring_end_date).slice(0, 10) : ''}
                onChange={(e) => handleFieldChange('recurring_end_date', e.target.value || null)}
                className="bg-white"
                data-testid="recurring-end-date-input"
              />
              <p className="text-[11px] text-gray-500">Si dejas en blanco, el evento aparecerá indefinidamente. Útil para ciclos con final, ej. "Voces durante el Desayuno hasta el 30 jun".</p>
            </div>
          )}

          {/* DATES MODE — list of specific dates with their hours */}
          {localEvent.is_recurring && localEvent.recurring_mode === 'dates' ? (
            <div className="space-y-2" data-testid="recurring-dates-editor">
              {recurringDates.length === 0 && (
                <p className="text-xs text-gray-500 italic">Agrega las fechas en que se realiza el evento. Ejemplo: una función cada día con su horario.</p>
              )}
              {recurringDates.map((entry, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2 p-2 bg-white rounded-lg border border-orange-200">
                  <Input
                    type="date"
                    value={entry.date || ''}
                    onChange={(e) => updateRecurringDate(idx, { date: e.target.value })}
                    className="w-44 h-9 text-sm"
                    data-testid={`recurring-date-${idx}`}
                  />
                  {entry.all_day ? (
                    <span className="text-sm font-medium text-green-600 px-2">Todo el día</span>
                  ) : (
                    <>
                      <Input
                        type="time"
                        value={entry.open || '20:00'}
                        onChange={(e) => updateRecurringDate(idx, { open: e.target.value })}
                        className="w-28 h-9 text-sm"
                        data-testid={`recurring-open-${idx}`}
                      />
                      <span className="text-gray-400 text-sm">a</span>
                      <Input
                        type="time"
                        value={entry.close || '22:00'}
                        onChange={(e) => updateRecurringDate(idx, { close: e.target.value })}
                        className="w-28 h-9 text-sm"
                        data-testid={`recurring-close-${idx}`}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => updateRecurringDate(idx, { all_day: !entry.all_day, open: entry.all_day ? '20:00' : '00:00', close: entry.all_day ? '22:00' : '23:59' })}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      entry.all_day
                        ? 'bg-green-100 text-green-700 border-green-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    24h
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRecurringDate(idx)}
                    className="ml-auto text-xs text-red-600 hover:bg-red-50 rounded-full px-2 py-1"
                    data-testid={`recurring-date-remove-${idx}`}
                  >
                    Eliminar
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addRecurringDate}
                className="w-full text-sm font-medium text-orange-700 hover:bg-orange-50 border-2 border-dashed border-orange-300 rounded-lg py-2"
                data-testid="recurring-add-date"
              >
                + Agregar fecha
              </button>
            </div>
          ) : (
          <div className="space-y-2">
            {DAY_KEYS.map(day => (
              <div key={day} className="flex items-center gap-3 flex-wrap">
                <div className="w-24 flex items-center gap-2">
                  <Switch
                    checked={!!operatingHours[day]}
                    onCheckedChange={() => handleDayToggle(day)}
                    data-testid={`day-toggle-${day}`}
                  />
                  <span className={`text-sm font-medium ${operatingHours[day] ? 'text-[#064E3B]' : 'text-gray-400'}`}>
                    {DAY_LABELS[day]}
                  </span>
                </div>
                {operatingHours[day] ? (
                  <div className="flex items-center gap-2">
                    {operatingHours[day].all_day ? (
                      <span className="text-sm font-medium text-green-600 px-2">Abierto 24 horas</span>
                    ) : (
                      <>
                        <Input
                          type="time"
                          value={operatingHours[day].open}
                          onChange={(e) => handleHourChange(day, 'open', e.target.value)}
                          className="w-28 h-8 text-sm"
                          data-testid={`hours-open-${day}`}
                        />
                        <span className="text-gray-400 text-sm">a</span>
                        <Input
                          type="time"
                          value={operatingHours[day].close}
                          onChange={(e) => handleHourChange(day, 'close', e.target.value)}
                          className="w-28 h-8 text-sm"
                          data-testid={`hours-close-${day}`}
                        />
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => handleAllDayToggle(day)}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                        operatingHours[day].all_day
                          ? 'bg-green-100 text-green-700 border-green-300'
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                      }`}
                      data-testid={`all-day-${day}`}
                    >
                      24h
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 italic">{localEvent.is_recurring ? 'Sin función' : 'Cerrado'}</span>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Schedule publication toggle - hides the event until publish_at */}
      <div className={`rounded-xl border-2 border-dashed p-4 ${schedulePast ? 'border-red-400 bg-red-50' : 'border-amber-400/50 bg-amber-50'}`} data-testid="schedule-publish-section">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Clock className={`w-5 h-5 ${schedulePast ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <p className={`font-semibold ${schedulePast ? 'text-red-800' : 'text-amber-800'}`}>Programar publicación</p>
              <p className={`text-xs ${schedulePast ? 'text-red-700/80' : 'text-amber-700/80'}`}>El evento queda oculto y se publica automáticamente en la fecha y hora que elijas (hora de Chile).</p>
            </div>
          </div>
          <Switch
            checked={!!localEvent.scheduled_publish_at}
            onCheckedChange={(checked) => {
              if (checked) {
                const d = new Date(Date.now() + 60 * 60 * 1000); // default: 1h from now, local
                const local = `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
                handleFieldChange('scheduled_publish_at', local);
              } else {
                handleFieldChange('scheduled_publish_at', '');
              }
            }}
            data-testid="schedule-publish-toggle"
          />
        </div>
        {localEvent.scheduled_publish_at && (
          <div className="mt-3 space-y-1">
            <Label className={`text-xs ${schedulePast ? 'text-red-800' : 'text-amber-800'}`}>Publicar el (hora de Chile)</Label>
            <Input
              type="datetime-local"
              value={localEvent.scheduled_publish_at}
              onChange={(e) => handleFieldChange('scheduled_publish_at', e.target.value)}
              min={nowLocal}
              className={`bg-white ${schedulePast ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
              aria-invalid={schedulePast || undefined}
              data-testid="schedule-publish-input"
            />
            {schedulePast && (
              <p className="text-xs font-medium text-red-700 flex items-center gap-1" data-testid="schedule-publish-error">
                <span aria-hidden>⚠️</span>
                La fecha y hora deben ser posteriores al momento actual.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Date fields - hidden for permanent and recurring events */}
      {!localEvent.is_permanent && !localEvent.is_recurring && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-2">
            <Label>{t('startDate')} *</Label>
            <Input
              type="datetime-local"
              value={localEvent.start_date}
              onChange={(e) => {
                const newStart = e.target.value;
                handleFieldChange('start_date', newStart);
                // Default end_date to start + 3h whenever start changes and
                // the current end is empty or an auto-default (23:59 same-day
                // or a previously-computed +3h from an earlier start value).
                if (newStart) {
                  const [dPart, tPart] = newStart.split('T');
                  const [y, m, d] = dPart.split('-').map(Number);
                  const [hh, mm] = (tPart || '00:00').split(':').map(Number);
                  const startDt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
                  const endDt = new Date(startDt.getTime() + 3 * 60 * 60 * 1000);
                  const pad = (n) => String(n).padStart(2, '0');
                  const autoEnd = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}T${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
                  const currentEnd = localEvent.end_date || '';
                  const endDay = currentEnd.slice(0, 10);
                  if (!currentEnd || endDay !== dPart || currentEnd.slice(11, 16) === '23:59') {
                    handleFieldChange('end_date', autoEnd);
                  }
                }
              }}
              required={!localEvent.is_permanent && !localEvent.is_recurring}
              data-testid="event-start-date"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('endDate')} *</Label>
            <Input
              type="datetime-local"
              value={localEvent.end_date}
              onChange={(e) => handleFieldChange('end_date', e.target.value)}
              required={!localEvent.is_permanent && !localEvent.is_recurring}
              data-testid="event-end-date"
            />
          </div>
        </div>
      )}

      {!isEdit && localEvent.is_tour && (
        <div className="rounded-xl border-2 border-violet-200 bg-violet-50/30 p-4 space-y-3" data-testid="tour-stops-section">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-sm text-violet-900">🎪 Paradas adicionales del tour</h4>
              <p className="text-[11px] text-violet-700">La fecha/comuna/dirección de arriba es la <b>Parada 1</b>. Agrega abajo el resto (una por cada ciudad).</p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-violet-400 text-violet-700 hover:bg-violet-100"
              onClick={() => {
                const stops = [...(localEvent.tour_stops || []), { start_date: '', end_date: '', venue_name: '', address: '', commune_id: '', latitude: null, longitude: null, price: '' }];
                handleFieldChange('tour_stops', stops);
              }}
              data-testid="add-tour-stop"
            >
              + Añadir parada
            </Button>
          </div>
          {(localEvent.tour_stops || []).map((stop, idx) => {
            // Region derived from the stop's commune (edit-like behaviour) or the manual pick.
            const stopRegionFromCommune = communes.find((c) => c.id === stop.commune_id)?.region_id || '';
            const stopRegionId = stop._region_id || stopRegionFromCommune;
            const stopFilteredCommunes = stopRegionId
              ? communes.filter((c) => c.region_id === stopRegionId)
              : communes;
            const updateStop = (patch) => {
              const stops = [...(localEvent.tour_stops || [])];
              stops[idx] = { ...stops[idx], ...patch };
              handleFieldChange('tour_stops', stops);
            };
            return (
            <div key={idx} className="rounded-lg border border-violet-300 bg-white p-3 space-y-2" data-testid={`tour-stop-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-violet-800 uppercase">Parada {idx + 2}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-red-600 hover:bg-red-50"
                  onClick={() => {
                    const stops = (localEvent.tour_stops || []).filter((_, i) => i !== idx);
                    handleFieldChange('tour_stops', stops);
                  }}
                >
                  🗑 Quitar
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Fecha y hora inicio *</Label>
                  <Input
                    type="datetime-local"
                    value={stop.start_date || ''}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      const currentEnd = stop.end_date || '';
                      const patch = { start_date: newStart };
                      if (newStart) {
                        const [dPart, tPart] = newStart.split('T');
                        const [y, m, d] = dPart.split('-').map(Number);
                        const [hh, mm] = (tPart || '00:00').split(':').map(Number);
                        const startDt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
                        const endDt = new Date(startDt.getTime() + 3 * 60 * 60 * 1000);
                        const pad = (n) => String(n).padStart(2, '0');
                        const autoEnd = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}T${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
                        const endDay = currentEnd.slice(0, 10);
                        if (!currentEnd || endDay !== dPart || currentEnd.slice(11, 16) === '23:59') {
                          patch.end_date = autoEnd;
                        }
                      }
                      updateStop(patch);
                    }}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Fecha y hora término</Label>
                  <Input
                    type="datetime-local"
                    value={stop.end_date || ''}
                    onChange={(e) => updateStop({ end_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Región *</Label>
                  <Select
                    value={stopRegionId || undefined}
                    onValueChange={(v) => {
                      // Clear commune if it no longer belongs to the new region.
                      const patch = { _region_id: v };
                      const currentCommune = communes.find((c) => c.id === stop.commune_id);
                      if (!currentCommune || currentCommune.region_id !== v) {
                        patch.commune_id = '';
                      }
                      updateStop(patch);
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Elige región" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {regions.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Comuna *</Label>
                  <Select
                    value={stop.commune_id || ''}
                    onValueChange={(v) => updateStop({ commune_id: v })}
                    disabled={!stopRegionId}
                  >
                    <SelectTrigger><SelectValue placeholder={stopRegionId ? 'Elige la comuna' : 'Elige región primero'} /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {stopFilteredCommunes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <VenueAutocomplete
                    label="Recinto / Lugar"
                    placeholder="Ej: Teatro Caupolicán"
                    initialText={stop.venue_name || ''}
                    communeId={stop.commune_id}
                    regionId={stopRegionId}
                    onTextChange={(v) => updateStop({ venue_name: v })}
                    onSelect={(v) => {
                      const patch = { venue_name: v.name };
                      if (v.address) patch.address = v.address;
                      if (v.latitude != null) patch.latitude = v.latitude;
                      if (v.longitude != null) patch.longitude = v.longitude;
                      if (v.commune_id) patch.commune_id = v.commune_id;
                      if (v.region_id) patch._region_id = v.region_id;
                      updateStop(patch);
                      toast.success(`Autocompletado desde: ${v.name}`);
                    }}
                    testId={`tour-stop-${idx}-venue-autocomplete`}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Precio (opcional)</Label>
                  <Input
                    type="number"
                    placeholder="Deja vacío para usar el precio general"
                    value={stop.price ?? ''}
                    onChange={(e) => updateStop({ price: e.target.value === '' ? null : e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px]">Dirección</Label>
                  <Input
                    placeholder="Calle 123, Comuna"
                    value={stop.address || ''}
                    onChange={(e) => updateStop({ address: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Latitud</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="-33.4489"
                    value={stop.latitude ?? ''}
                    onChange={(e) => updateStop({ latitude: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Longitud</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="-70.6693"
                    value={stop.longitude ?? ''}
                    onChange={(e) => updateStop({ longitude: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full border-violet-300 text-violet-700 hover:bg-violet-50"
                    onClick={() => setStopMapOpenIdx(idx)}
                    data-testid={`tour-stop-${idx}-map-btn`}
                  >
                    📍 {stop.latitude && stop.longitude ? 'Cambiar ubicación en el mapa' : 'Seleccionar ubicación en el mapa'}
                  </Button>
                </div>
              </div>
            </div>
            );
          })}
          {(localEvent.tour_stops || []).length === 0 && (
            <p className="text-xs text-violet-600 italic">Aún no hay paradas extra. Presiona &quot;+ Añadir parada&quot; para agregar la siguiente ciudad.</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('price')} (CLP)</Label>
          <Input
            type="number"
            placeholder="Dejar vacío para omitir"
            value={localEvent.price ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              handleFieldChange('price', val === '' ? null : val);
              handleFieldChange('is_free', val === '0');
            }}
          />
          <p className="text-xs text-gray-400">Vacío = sin precio · 0 = Gratis</p>
          {parseInt(localEvent.price) > 0 && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localEvent.price_is_from || false}
                onChange={(e) => handleFieldChange('price_is_from', e.target.checked)}
                className="rounded border-gray-300 text-[#064E3B] focus:ring-[#064E3B]"
                data-testid="price-is-from-checkbox"
              />
              <span className="text-sm text-gray-600">Precio desde (tarifas diferenciadas)</span>
            </label>
          )}
        </div>
        {/* Ticket-sale URL — same field the bulk upload writes from column Q
            of the Excel template. When the event is online this doubles as
            the "Link para unirse" (shown in the purple online block above).
            Exposing it here lets providers see/edit it even for offline
            events. */}
        {!localEvent.is_online && (
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <span>URL de venta de entradas</span>
              <span className="text-xs font-normal text-gray-400">(opcional)</span>
            </Label>
            <Input
              type="url"
              placeholder="https://passline.com/... o https://ticketplus.cl/..."
              value={localEvent.online_url || ''}
              onChange={(e) => handleFieldChange('online_url', e.target.value)}
              data-testid="ticket-url-input"
            />
            {localEvent.online_url ? (
              <a
                href={localEvent.online_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                data-testid="ticket-url-preview"
              >
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span>Ver venta de entradas</span>
              </a>
            ) : (
              <p className="text-xs text-gray-400">Se usa para el botón "Comprar entradas".</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Región *</Label>
          <Select
            value={selectedRegionId || undefined}
            onValueChange={(v) => {
              setSelectedRegionId(v);
              // If the currently picked commune isn't in the new region, clear it.
              const currentCommune = communes.find((c) => c.id === localEvent.commune_id);
              if (!currentCommune || currentCommune.region_id !== v) {
                handleFieldChange('commune_id', '');
              }
            }}
          >
            <SelectTrigger data-testid="event-region-select">
              <SelectValue placeholder="Seleccionar región" />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Comuna *</Label>
          <Select
              value={localEvent.commune_id}
              onValueChange={(v) => handleFieldChange('commune_id', v)}
              disabled={!selectedRegionId}
            >
              <SelectTrigger data-testid="event-commune-select">
                <SelectValue placeholder={selectedRegionId ? 'Seleccionar' : 'Elige región primero'} />
              </SelectTrigger>
              <SelectContent>
                {filteredCommunes.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
        </div>
      </div>

      <div className="space-y-2 max-w-md">
        <Label>{t('audience')}</Label>
        <Select
          value={localEvent.target_audience}
          onValueChange={(v) => handleFieldChange('target_audience', v)}
        >
          <SelectTrigger data-testid="event-audience-select">
            <SelectValue placeholder="Seleccionar público" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="kids">Niños</SelectItem>
            <SelectItem value="teen">Adolescentes</SelectItem>
            <SelectItem value="adults">Adultos</SelectItem>
            <SelectItem value="family">Familiar</SelectItem>
            <SelectItem value="all">Todo público</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3 rounded-xl border border-pink-100 bg-pink-50/40 p-3">
        <OrganizerAutocomplete
          label="Nombre del Organizador"
          placeholder="Ej: Cirque du Soleil… (autocompleta el IG)"
          onSelect={(org) => {
            if (org.instagram) {
              handleFieldChange('organizer_instagram', org.instagram);
              toast.success(`IG autocompletado: ${org.instagram}`);
            }
          }}
          testId="event-organizer-autocomplete"
        />
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            Instagram del Organizador
          </Label>
          <Input
            placeholder="@organizador o https://instagram.com/organizador"
            value={localEvent.organizer_instagram || ''}
            onChange={(e) => handleFieldChange('organizer_instagram', e.target.value)}
            data-testid="organizer-instagram-input"
          />
          <p className="text-[11px] text-gray-500">Se autocompleta al elegir un organizador del catálogo. Puedes editarlo manualmente.</p>
        </div>
      </div>

      <div className="space-y-2">
        <VenueAutocomplete
          label="Nombre del recinto"
          placeholder="Escribe el nombre del lugar (ej: Teatro Sidarte)…"
          initialText={localEvent.venue_name || ''}
          communeId={localEvent.commune_id}
          onTextChange={(v) => handleFieldChange('venue_name', v)}
          onSelect={(v) => {
            if (v.address) handleFieldChange('address', v.address);
            if (v.latitude != null) handleFieldChange('latitude', v.latitude);
            if (v.longitude != null) handleFieldChange('longitude', v.longitude);
            if (v.commune_id) handleFieldChange('commune_id', v.commune_id);
            handleFieldChange('venue_name', v.name);
            toast.success(`Autocompletado desde: ${v.name}`);
          }}
          testId="event-form-venue-autocomplete"
        />
      </div>

      <div className="space-y-2">
        <Label>{t('address')}</Label>
        <Input
          value={localEvent.address || ''}
          onChange={(e) => handleFieldChange('address', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Latitud</Label>
          <Input
            type="number"
            step="any"
            placeholder="-33.4489"
            value={localEvent.latitude || ''}
            onChange={(e) => handleFieldChange('latitude', e.target.value)}
            readOnly
          />
        </div>
        <div className="space-y-2">
          <Label>Longitud</Label>
          <Input
            type="number"
            step="any"
            placeholder="-70.6693"
            value={localEvent.longitude || ''}
            onChange={(e) => handleFieldChange('longitude', e.target.value)}
            readOnly
          />
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => setMapModalOpen(true)}
        data-testid="open-map-btn"
      >
        <MapPin className="w-4 h-4 mr-2" />
        {localEvent.latitude && localEvent.longitude ? 'Cambiar ubicacion en el mapa' : 'Seleccionar ubicacion en el mapa'}
      </Button>

      <LocationPickerModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        onConfirm={(lat, lng, address) => {
          handleFieldChange('latitude', lat);
          handleFieldChange('longitude', lng);
          if (address) handleFieldChange('address', address);
        }}
        initialLat={localEvent.latitude ? parseFloat(localEvent.latitude) : null}
        initialLng={localEvent.longitude ? parseFloat(localEvent.longitude) : null}
        initialCommuneId={localEvent.commune_id}
        initialAddress={localEvent.address}
        communes={communes}
      />

      {/* Map picker for a specific tour stop. Rendered once; controlled via stopMapOpenIdx. */}
      {stopMapOpenIdx !== null && (localEvent.tour_stops || [])[stopMapOpenIdx] && (
        <LocationPickerModal
          open={true}
          onClose={() => setStopMapOpenIdx(null)}
          onConfirm={(lat, lng, address) => {
            const stops = [...(localEvent.tour_stops || [])];
            stops[stopMapOpenIdx] = {
              ...stops[stopMapOpenIdx],
              latitude: lat,
              longitude: lng,
              ...(address ? { address } : {}),
            };
            handleFieldChange('tour_stops', stops);
            setStopMapOpenIdx(null);
          }}
          initialLat={(localEvent.tour_stops[stopMapOpenIdx].latitude != null) ? parseFloat(localEvent.tour_stops[stopMapOpenIdx].latitude) : null}
          initialLng={(localEvent.tour_stops[stopMapOpenIdx].longitude != null) ? parseFloat(localEvent.tour_stops[stopMapOpenIdx].longitude) : null}
          initialCommuneId={localEvent.tour_stops[stopMapOpenIdx].commune_id}
          initialAddress={localEvent.tour_stops[stopMapOpenIdx].address}
          communes={communes}
        />
      )}

      <div className="space-y-3">
        {localEvent.image_purged && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
            data-testid="image-purged-banner"
          >
            <ImageIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
            <div>
              <div className="font-semibold">La foto fue eliminada por antigüedad</div>
              <div className="text-xs text-amber-800 mt-0.5">
                El evento terminó hace más de 30 días y su imagen fue purgada para
                liberar espacio. Sube una nueva foto antes de reactivar; puede tener
                info del evento anterior que conviene actualizar.
              </div>
            </div>
          </div>
        )}
        <Label className={imageError ? 'text-red-600 font-semibold' : ''}>
          Fotos del evento * <span className="text-xs font-normal text-gray-400">(hasta 3 fotos, la primera es portada)</span>
        </Label>
        {imageError && !localEvent.main_image && (
          <p className="text-sm text-red-500" data-testid="image-required-error">La imagen es obligatoria para publicar un evento.</p>
        )}

        {/* 3-image grid */}
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {/* Main image (slot 0 - portada) */}
            <div className="relative">
              <input ref={eventImageRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => handleImageUpload(e, 'main')} />
              {(localPreviews.main || localEvent.main_image) ? (
                <div className="relative rounded-lg overflow-hidden border-2 border-[#064E3B] h-32 bg-gray-50">
                  <img
                    src={localPreviews.main || (localEvent.main_image.startsWith('/api/') ? `${API_URL}${localEvent.main_image}` : localEvent.main_image)}
                    alt="Portada"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `50% ${localEvent.image_position || '50%'}` }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {uploadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    </div>
                  )}
                  <span className="absolute top-1 left-1 bg-[#064E3B] text-white text-[10px] px-1.5 py-0.5 rounded-full">Portada</span>
                  <Button type="button" variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 bg-black/50 hover:bg-black/70 text-white rounded-full" onClick={() => {
                    if (localPreviews.main) { try { URL.revokeObjectURL(localPreviews.main); } catch { /* noop */ } }
                    setLocalPreviews(prev => { const n = { ...prev }; delete n.main; return n; });
                    handleFieldChange('main_image', '');
                  }}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <button type="button" className="w-full h-32 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#064E3B] flex flex-col items-center justify-center gap-1 transition-colors" onClick={() => eventImageRef.current?.click()} disabled={uploadingImage}>
                  {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
                    <>
                      <Camera className="w-5 h-5 text-gray-400" />
                      <span className="text-[10px] text-gray-400">Portada *</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Additional images (slots 1-2) */}
            {[0, 1].map(idx => {
              const imgs = localEvent.additional_images || [];
              const item = imgs[idx];
              const imgUrl = typeof item === 'string' ? item : item?.url;
              const imgPos = typeof item === 'object' ? item?.position || '50%' : '50%';
              const previewUrl = localPreviews[String(idx)];
              const displayUrl = previewUrl || imgUrl;
              return (
                <div key={idx} className="relative">
                  <input ref={additionalImageRefs[idx]} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => handleImageUpload(e, String(idx))} />
                  {displayUrl ? (
                    <div className="relative rounded-lg overflow-hidden border h-32 bg-gray-50">
                      <img src={displayUrl} alt={`Foto ${idx + 2}`} className="w-full h-full object-cover" style={{ objectPosition: `50% ${imgPos}` }} onError={(e) => { e.target.style.display = 'none'; }} />
                      {uploadingImage && previewUrl && !imgUrl && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Loader2 className="w-5 h-5 animate-spin text-white" />
                        </div>
                      )}
                      <Button type="button" variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 bg-black/50 hover:bg-black/70 text-white rounded-full" onClick={() => {
                        if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ } }
                        setLocalPreviews(prev => { const n = { ...prev }; delete n[String(idx)]; return n; });
                        const updated = [...(localEvent.additional_images || [])];
                        updated.splice(idx, 1);
                        handleFieldChange('additional_images', updated);
                      }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <button type="button" className="w-full h-32 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-400 flex flex-col items-center justify-center gap-1 transition-colors" onClick={() => additionalImageRefs[idx].current?.click()} disabled={uploadingImage}>
                      {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
                        <>
                          <Plus className="w-5 h-5 text-gray-300" />
                          <span className="text-[10px] text-gray-400">Foto {idx + 2}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Position sliders per image */}
          <div className="grid grid-cols-3 gap-3">
            {/* Main image slider */}
            <div>
              {localEvent.main_image && (
                <input type="range" min="0" max="100" value={parseInt(localEvent.image_position) || 50}
                  onChange={(e) => handleFieldChange('image_position', `${e.target.value}%`)}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#064E3B]"
                  data-testid="img-position-slider"
                  title="Ajustar altura de portada"
                />
              )}
            </div>
            {/* Additional image sliders */}
            {[0, 1].map(idx => {
              const imgs = localEvent.additional_images || [];
              const item = imgs[idx];
              const imgUrl = typeof item === 'string' ? item : item?.url;
              const imgPos = typeof item === 'object' ? item?.position || '50%' : '50%';
              return (
                <div key={idx}>
                  {imgUrl && (
                    <input type="range" min="0" max="100" value={parseInt(imgPos) || 50}
                      onChange={(e) => {
                        const updated = [...(localEvent.additional_images || [])];
                        const currentUrl = typeof updated[idx] === 'string' ? updated[idx] : updated[idx]?.url;
                        updated[idx] = { url: currentUrl, position: `${e.target.value}%` };
                        handleFieldChange('additional_images', updated);
                      }}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#064E3B]"
                      title={`Ajustar altura de foto ${idx + 2}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* URL input as alternative */}
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-gray-400 shrink-0" />
          <Input
            type="url"
            placeholder="O pega URL de imagen..."
            className="h-8 text-sm"
            value=""
            onChange={(e) => {
              if (e.target.value && e.target.value.startsWith('http')) {
                if (!localEvent.main_image) {
                  handleFieldChange('main_image', e.target.value);
                } else {
                  const current = localEvent.additional_images || [];
                  if (current.length < 2) {
                    handleFieldChange('additional_images', [...current, e.target.value]);
                  }
                }
                e.target.value = '';
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const val = e.target.value;
                if (val && val.startsWith('http')) {
                  if (!localEvent.main_image) {
                    handleFieldChange('main_image', val);
                  } else {
                    const current = localEvent.additional_images || [];
                    if (current.length < 2) {
                      handleFieldChange('additional_images', [...current, val]);
                    }
                  }
                  e.target.value = '';
                }
              }
            }}
            data-testid="image-url-input"
          />
        </div>

        {/* Position slider moved into grid above */}
      </div>

      {/* Footer: appears at the natural end of the form. Not sticky — user
          explicitly asked for the buttons to sit BELOW all content, not float
          on top of it. */}
      <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto" data-testid="cancel-event-btn">
          {t('cancel')}
        </Button>
        <Button type="submit" className="btn-primary w-full sm:w-auto" disabled={submitting} data-testid="submit-event-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {isEdit ? 'Guardar cambios' : 'Crear evento'}
        </Button>
      </div>
    </form>
  );
});

EventForm.displayName = 'EventForm';

export const ProviderDashboard = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const { formatPrice } = useCurrency();
  const fileInputRef = useRef(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [venues, setVenues] = useState([]);
  // Admin-only: list of providers for the "Publicar como" dropdown. Empty
  // for non-admins. Default falls back to PlanCity (plancity@plancity.cl).
  const [adminProviders, setAdminProviders] = useState([]);
  const [defaultAdminProviderId, setDefaultAdminProviderId] = useState('');
  const isAdmin = user?.role === 'admin';
  // Bulk-upload state (admin only). Parsed drafts + errors from the Excel
  // template. Drafts stay in state until the admin publishes them or dismisses
  // them, so re-opening the modal doesn't lose the queue.
  const [bulkDrafts, setBulkDrafts] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkParsing, setBulkParsing] = useState(false);
  // System modal state to replace window.confirm on duplicate detection.
  // • duplicateInfo (single create): { existing: {...}, name }
  // • bulkDuplicateReport: { newCount, duplicates: [{ row, name, existing }] }
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [bulkDuplicateReport, setBulkDuplicateReport] = useState(null);
  const [bulkPreflightLoading, setBulkPreflightLoading] = useState(false);
  const [exportingEvents, setExportingEvents] = useState(false);

  // Admin-only: download every event as an .xlsx file using the exact
  // template layout from `services/events_exporter.py`. The blob is streamed
  // by the backend; we save it via an anchor click and clean up the URL.
  const handleExportEvents = async () => {
    setExportingEvents(true);
    try {
      const res = await axios.get(`${API_URL}/api/events/export.xlsx`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `plancity-eventos-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo exportar el Excel');
    } finally {
      setExportingEvents(false);
    }
  };
  const [membership, setMembership] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Default active tab: skip onboarding once the user has marked the intro as seen
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return localStorage.getItem('plancity_onboarding_seen') === '1' ? 'events' : 'onboarding';
    } catch {
      return 'onboarding';
    }
  });
  const [ticketsEvent, setTicketsEvent] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Generic confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    variant: 'default'
  });

  const showConfirmDialog = (title, description, onConfirm, variant = 'default') => {
    setConfirmDialog({
      open: true,
      title,
      description,
      onConfirm,
      variant
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // ---- Persisted list state ------------------------------------------------
  // Pagination + filters for "Mis Eventos" live in sessionStorage so a save,
  // delete or navigation elsewhere in the app doesn't drop the operator back
  // on page 1 with cleared filters. Scoped per-user via the localStorage
  // `user` object so admin and provider views don't cross-contaminate.
  const _persistKey = React.useMemo(() => `plancity.provider.events.list:${user?.id || 'anon'}`, [user?.id]);
  const _restoredList = React.useMemo(() => {
    if (typeof window === 'undefined') return {};
    try {
      return JSON.parse(sessionStorage.getItem(_persistKey) || '{}') || {};
    } catch { return {}; }
  }, [_persistKey]);

  const [currentPage, setCurrentPage] = useState(_restoredList.currentPage || 1);
  const eventsPerPage = 10;

  // Search and filter state for "Mis Eventos"
  const [searchQuery, setSearchQuery] = useState(_restoredList.searchQuery || '');
  const [statusFilter, setStatusFilter] = useState(_restoredList.statusFilter || 'active');
  const [dateFilter, setDateFilter] = useState(_restoredList.dateFilter || '');
  const [typeFilter, setTypeFilter] = useState(_restoredList.typeFilter || 'all');
  const [regionFilter, setRegionFilter] = useState(_restoredList.regionFilter || 'all');
  const [communeFilter, setCommuneFilter] = useState(_restoredList.communeFilter || 'all');
  // Admin-only: filter events by provider so the /provider tab acts as a
  // global event manager sliced per publisher. Non-admins never see the
  // control (they only manage their own provider).
  const [providerFilter, setProviderFilter] = useState(_restoredList.providerFilter || 'all');

  // Persist whenever any of these change. Uses sessionStorage so it clears
  // on tab close but survives page reloads and CRUD-driven refreshes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(_persistKey, JSON.stringify({
        currentPage, searchQuery, statusFilter, dateFilter,
        typeFilter, regionFilter, communeFilter, providerFilter,
      }));
    } catch {}
  }, [_persistKey, currentPage, searchQuery, statusFilter, dateFilter, typeFilter, regionFilter, communeFilter, providerFilter]);

  // Coarse clamp: if the total number of events shrinks past what the
  // current page can hold (e.g. after deleting the last event on this page),
  // step back one page. Kept dumb-simple with `events.length` so it runs
  // above the `if (loading)` early return without triggering React's
  // "hooks changed" invariant. A finer clamp against `filteredEvents`
  // happens implicitly on the next render.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(events.length / eventsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [events.length, currentPage, eventsPerPage]);
  
  // Profile form state
  const [profile, setProfile] = useState({
    business_name: '',
    description: '',
    phone: '',
    website: '',
    instagram: '',
    logo_url: ''
  });

  const [newEvent, setNewEvent] = useState({
    name: '',
    short_description: '',
    description: '',
    start_date: '',
    end_date: '',
    schedule: '',
    price: null,
    price_is_from: false,
    is_free: false,
    address: '',
    latitude: '',
    longitude: '',
    target_audience: 'family',
    organizer_instagram: '',
    main_image: '',
    category_id: '',
    category_id_2: '',
    commune_id: '',
    is_permanent: false,
    operating_hours: null,
    scheduled_publish_at: '',
    manual_override_closed: false,
    is_recurring: false,
    recurring_mode: 'weekly',
    recurring_dates: [],
    recurring_end_date: '',
    is_online: false,
    online_url: '',
    online_platform: '',
    image_position: '50%',
    additional_images: [],
    // Admin-only "Publicar como" (Publish as). Empty = default (PlanCity fallback).
    publish_as_provider_id: ''
  });

  // Sync the "Publicar como" default into newEvent once the admin providers
  // list resolves. We only set it when the field is still empty so we don't
  // clobber a manual pick the admin already made in this session.
  useEffect(() => {
    if (isAdmin && defaultAdminProviderId && !newEvent.publish_as_provider_id) {
      setNewEvent(prev => ({ ...prev, publish_as_provider_id: defaultAdminProviderId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, defaultAdminProviderId]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Initialize profile from user data
    if (user?.provider) {
      setProfile({
        business_name: user.provider.business_name || '',
        description: user.provider.description || '',
        phone: user.provider.phone || '',
        website: user.provider.website || '',
        instagram: user.provider.instagram || '',
        logo_url: user.provider.logo_url || ''
      });
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // allSettled so a single transient failure doesn't collapse the whole dashboard.
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/providers/me/stats`),
        axios.get(`${API_URL}/api/providers/me/events`),
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/territories/communes`),
        axios.get(`${API_URL}/api/memberships/me`),
        axios.get(`${API_URL}/api/memberships/plans`),
        axios.get(`${API_URL}/api/messages/inbox`),
        axios.get(`${API_URL}/api/messages/inbox/count`),
        axios.get(`${API_URL}/api/events/plan-limits/me`),
        axios.get(`${API_URL}/api/venues?limit=500`),
        axios.get(`${API_URL}/api/territories/regions`),
      ]);
      const val = (idx, fallback) => (results[idx].status === 'fulfilled' ? results[idx].value.data : fallback);
      setStats(val(0, {}));
      setEvents(val(1, []));
      if (results[2].status === 'fulfilled') setCategories(val(2, []));
      if (results[3].status === 'fulfilled') setCommunes(val(3, []));
      setMembership(val(4, null));
      setPlans(val(5, {})?.plans || []);
      setMessages(val(6, []));
      setUnreadCount(val(7, { unread_count: 0 })?.unread_count || 0);
      setPlanLimits(val(8, null));
      if (results[9] && results[9].status === 'fulfilled') setVenues(val(9, []));
      if (results[10] && results[10].status === 'fulfilled') setRegions(val(10, []));

      // Admin-only: pull the full providers list so we can render the
      // "Publicar como" dropdown in the Crear/Editar Evento form. We keep this
      // out of the main Promise.allSettled block because it's admin-scoped —
      // hitting /api/admin/providers as a plain provider returns 403.
      if (user?.role === 'admin') {
        try {
          const provRes = await axios.get(`${API_URL}/api/admin/providers`);
          const list = Array.isArray(provRes.data) ? provRes.data : [];
          setAdminProviders(list);
          const plancity = list.find(p => (p.assigned_users || []).some(u => u.email === 'plancity@plancity.cl'));
          if (plancity) setDefaultAdminProviderId(plancity.id);
        } catch (err) {
          console.warn('admin providers fetch failed:', err?.message);
        }
      }
      results.forEach((r, i) => {
        if (r.status === 'rejected') console.warn(`provider fetch #${i} failed:`, r.reason?.message || r.reason);
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSaveSuccess(false);
    
    try {
      // Prepare profile data - don't send logo_url if it's already saved via upload
      // The logo is saved separately through the upload endpoint
      const profileData = {
        business_name: profile.business_name,
        description: profile.description,
        phone: profile.phone,
        website: profile.website,
        instagram: profile.instagram
      };
      
      // Only include logo_url if it's a valid external URL (not base64 and not empty)
      if (profile.logo_url && 
          !profile.logo_url.startsWith('data:') && 
          profile.logo_url.trim() !== '') {
        // If it's a relative URL from our server, don't include it (already saved)
        if (!profile.logo_url.includes('/api/static/uploads/')) {
          profileData.logo_url = profile.logo_url;
        }
      }
      
      await axios.put(`${API_URL}/api/providers/me`, profileData);
      
      // Refresh user data to get updated profile
      if (refreshUser) {
        await refreshUser();
      }
      
      setSaveSuccess(true);
      toast.success('¡Perfil actualizado correctamente!', {
        description: 'Los cambios se han guardado exitosamente.',
        duration: 4000,
      });
      
      // Reset success state after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Failed to update profile:', error);
      const errorMsg = getErrorMessage(error, 'Error al actualizar perfil');
      toast.error('Error al guardar', {
        description: errorMsg,
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    
    try {
      // Upload to server using FormData
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/uploads/profile-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local state with the returned URL
      const imageUrl = `${API_URL}${response.data.url}`;
      setProfile(prev => ({ ...prev, logo_url: imageUrl }));
      
      // Refresh user data to get updated profile
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('¡Foto actualizada!', {
        description: 'Tu foto de perfil se ha guardado correctamente.',
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Failed to upload photo:', error);
      const errorMsg = getErrorMessage(error, 'Error al subir la imagen');
      toast.error('Error al subir imagen', {
        description: errorMsg,
        duration: 5000,
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Mark message as read
  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(`${API_URL}/api/messages/${messageId}/read`);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    showConfirmDialog(
      'Eliminar mensaje',
      '¿Estás seguro de eliminar este mensaje?',
      async () => {
        try {
          await axios.delete(`${API_URL}/api/messages/${messageId}`);
          const deletedMsg = messages.find(m => m.id === messageId);
          setMessages(prev => prev.filter(m => m.id !== messageId));
          if (deletedMsg && !deletedMsg.is_read) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
          toast.success('Mensaje eliminado');
        } catch (error) {
          console.error('Failed to delete message:', error);
          toast.error('Error al eliminar el mensaje');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const handleToggleDiscounts = async (eventId) => {
    try {
      const response = await axios.put(`${API_URL}/api/discounts/${eventId}/toggle`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar descuentos'));
    }
  };

  const handleToggleFeature = async (event, targetType) => {
    // targetType: 'homepage' | 'carousel'
    // If event is already featured with that exact type, toggle OFF; otherwise switch ON to that type
    const currentType = event.is_featured ? (event.featured_type || 'homepage') : null;
    const nextType = currentType === targetType ? null : targetType;
    try {
      const response = await axios.put(`${API_URL}/api/events/${event.id}/feature`, {
        featured_type: nextType,
      });
      toast.success(response.data.message);
      // In-place update instead of `fetchData()` so we don't flash the loading
      // skeleton or reset pagination back to page 1. Just patch the toggled
      // event and let React re-render.
      setEvents((prev) => prev.map((ev) => (
        ev.id === event.id
          ? { ...ev, is_featured: nextType != null, featured_type: nextType }
          : ev
      )));
      // Refresh plan-limits counters silently (no loading skeleton).
      try {
        const pl = await axios.get(`${API_URL}/api/events/plan-limits/me`);
        setPlanLimits(pl.data);
      } catch (_) {
        // silent — chip counters will refresh on the next full fetch
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar destacado'));
    }
  };

  const handleToggleClosed = async (eventId) => {
    try {
      const response = await axios.put(`${API_URL}/api/events/${eventId}/toggle-closed`);
      toast.success(response.data.message);
      // In-place patch — same rationale as handleToggleFeature.
      setEvents((prev) => prev.map((ev) => (
        ev.id === eventId ? { ...ev, is_closed: !ev.is_closed } : ev
      )));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar estado'));
    }
  };

  const handleDuplicateEvent = (event) => {
    setNewEvent({
      name: event.name,
      short_description: event.short_description || '',
      description: event.description || '',
      start_date: '',
      end_date: '',
      schedule: event.schedule || '',
      price: event.price,
      price_is_from: event.price_is_from || false,
      is_free: event.is_free || false,
      address: event.address || '',
      latitude: event.latitude || '',
      longitude: event.longitude || '',
      target_audience: event.target_audience || 'family',
      organizer_instagram: event.organizer_instagram || '',
      main_image: event.main_image || '',
      image_position: event.image_position || '50%',
      additional_images: event.additional_images || [],
      category_id: event.category_id || '',
      category_id_2: event.category_id_2 || '',
      commune_id: event.commune_id || '',
      is_permanent: event.is_permanent || false,
      operating_hours: event.operating_hours || null,
      manual_override_closed: false,
      is_recurring: event.is_recurring || false,
      recurring_mode: event.recurring_mode || 'weekly',
      recurring_dates: event.recurring_dates || [],
      recurring_end_date: event.recurring_end_date ? String(event.recurring_end_date).slice(0, 10) : '',
      is_online: event.is_online || false,
      online_url: event.online_url || '',
      online_platform: event.online_platform || ''
    });
    setCreateDialogOpen(true);
    toast.info('Evento duplicado. Modifica los datos y guarda.');
  };

  const handleCreateEvent = async (eventFormData) => {
    setSubmitting(true);

    try {
      // Peel off private/UI-only fields so they never leak into the API payload.
      // `__bulk_row` is a UI marker for the Excel bulk queue and Pydantic
      // silently drops it today, but the coupling is fragile.
      const { __bulk_row: bulkRow, ...formForApi } = eventFormData;
      const eventData = {
        ...formForApi,
        category_id_2: eventFormData.category_id_2 || null,
        price: eventFormData.price !== null && eventFormData.price !== '' ? parseFloat(eventFormData.price) : null,
        price_is_from: eventFormData.price_is_from || false,
        latitude: eventFormData.latitude ? parseFloat(eventFormData.latitude) : null,
        longitude: eventFormData.longitude ? parseFloat(eventFormData.longitude) : null,
        is_permanent: eventFormData.is_permanent || false,
        operating_hours: (eventFormData.is_permanent || eventFormData.is_recurring) ? eventFormData.operating_hours : null,
        manual_override_closed: eventFormData.manual_override_closed || false,
        is_recurring: eventFormData.is_recurring || false,
        recurring_mode: eventFormData.is_recurring ? (eventFormData.recurring_mode || 'weekly') : null,
        recurring_dates: (eventFormData.is_recurring && eventFormData.recurring_mode === 'dates') ? (eventFormData.recurring_dates || []) : null,
        recurring_end_date: (eventFormData.is_recurring && (eventFormData.recurring_mode || 'weekly') === 'weekly' && eventFormData.recurring_end_date) ? eventFormData.recurring_end_date : null,
        is_online: eventFormData.is_online || false,
        // `online_url` doubles as the "Comprar entradas" CTA URL for
        // presencial events (yellow badge on EventDetail). Preserve the
        // value regardless of `is_online` so bulk-uploaded ticket links
        // (Passline/Ticketplus/etc.) survive.
        online_url: (eventFormData.online_url || '').trim() || null,
        online_platform: eventFormData.is_online ? (eventFormData.online_platform || null) : null,
        image_position: eventFormData.image_position || '50%',
        additional_images: eventFormData.additional_images || [],
        scheduled_publish_at: eventFormData.scheduled_publish_at || null,
      };

      // Tour mode: build a shared+stops payload and POST to /api/events/tour
      // Each stop becomes a standalone event linked by tour_id — the creator
      // filled the shared fields once + the first parada as the main form.
      if (eventFormData.is_tour) {
        const extraStops = (eventFormData.tour_stops || []).filter(s => s.start_date && s.commune_id);
        if (extraStops.length === 0) {
          toast.error('Debes agregar al menos 1 parada adicional al tour (o desactivar Modo Gira).');
          setSubmitting(false);
          return;
        }
        const stopFromMain = {
          start_date: eventFormData.start_date,
          end_date: eventFormData.end_date,
          venue_name: eventFormData.venue_name || null,
          address: eventFormData.address,
          latitude: eventFormData.latitude ? parseFloat(eventFormData.latitude) : null,
          longitude: eventFormData.longitude ? parseFloat(eventFormData.longitude) : null,
          commune_id: eventFormData.commune_id,
          price: eventData.price,
          is_free: eventData.is_free,
          online_url: eventData.online_url,
        };
        const mappedStops = extraStops.map(s => ({
          start_date: s.start_date,
          end_date: s.end_date || null,
          venue_name: s.venue_name || null,
          address: s.address || null,
          latitude: s.latitude != null ? parseFloat(s.latitude) : null,
          longitude: s.longitude != null ? parseFloat(s.longitude) : null,
          commune_id: s.commune_id,
          price: (s.price !== '' && s.price != null) ? parseFloat(s.price) : null,
          is_free: null,
          online_url: null,
        }));
        // Strip tour-only fields from the shared payload sent to backend.
        const { is_tour: _t, tour_stops: _ts, ...shared } = eventData;
        const res = await axios.post(`${API_URL}/api/events/tour`, {
          shared,
          stops: [stopFromMain, ...mappedStops],
        });
        setCreateDialogOpen(false);
        resetEventForm();
        toast.success(`¡Gira publicada! ${res.data.stops_created} paradas creadas.`);
        fetchData();
        setSubmitting(false);
        return;
      }

      const postEvent = async () => {
        return axios.post(`${API_URL}/api/events`, eventData);
      };

      let res;
      try {
        res = await postEvent();
      } catch (err) {
        // Duplicate detection: backend returns 409 with { code, message, existing }.
        // We NEVER allow duplicates — surface a system modal (not the browser's
        // window.confirm) so the admin sees which event conflicts and can go
        // fix or cancel. The bulk pre-flight has already filtered dupes out for
        // Excel imports, so this branch mostly fires for individual submissions.
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail?.code === 'duplicate_event') {
          setDuplicateInfo({ existing: detail.existing || {}, attemptedName: eventData.name });
          setSubmitting(false);
          return;
        }
        throw err;
      }
      // Bulk mode: if this event came from an Excel draft, consume it and
      // auto-load the next draft into the form (keeping the modal open so the
      // admin can rip through the queue). Otherwise close the modal.
      const publishedFromBulkRow = bulkRow;
      let remainingDrafts = bulkDrafts;
      if (publishedFromBulkRow) {
        remainingDrafts = bulkDrafts.filter(d => d.row !== publishedFromBulkRow);
        setBulkDrafts(remainingDrafts);
      }
      const nextDraft = publishedFromBulkRow ? remainingDrafts[0] : null;
      if (nextDraft) {
        resetEventForm();
        applyDraftToForm(nextDraft);
      } else {
        setCreateDialogOpen(false);
        resetEventForm();
      }
      const createdStatus = res?.data?.status;
      const scheduled = !!eventData.scheduled_publish_at;
      let toastDesc;
      if (scheduled) {
        toastDesc = 'Se publicará automáticamente en la fecha y hora indicadas.';
      } else if (createdStatus === 'active') {
        toastDesc = 'Tu evento ya está publicado y visible para el público.';
      } else {
        toastDesc = 'Tu evento ha sido enviado para revisión.';
      }
      if (nextDraft) {
        toastDesc = `Continuando con ${remainingDrafts.length} evento${remainingDrafts.length === 1 ? '' : 's'} restante${remainingDrafts.length === 1 ? '' : 's'}.`;
      }
      toast.success(scheduled ? '¡Evento programado!' : (createdStatus === 'active' ? '¡Evento publicado!' : '¡Evento creado!'), {
        description: toastDesc
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Error al crear evento', {
        description: getErrorMessage(error)
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditEvent = async (eventFormData) => {
    if (!eventFormData || !eventFormData.id) return;
    setSubmitting(true);

    try {
      const eventData = {
        name: eventFormData.name,
        short_description: eventFormData.short_description,
        description: eventFormData.description,
        start_date: (eventFormData.is_permanent || eventFormData.is_recurring) ? null : eventFormData.start_date,
        end_date: (eventFormData.is_permanent || eventFormData.is_recurring) ? null : eventFormData.end_date,
        schedule: eventFormData.schedule,
        price: eventFormData.price !== null && eventFormData.price !== '' ? parseFloat(eventFormData.price) : null,
        price_is_from: eventFormData.price_is_from || false,
        is_free: eventFormData.is_free,
        address: eventFormData.address,
        latitude: eventFormData.latitude ? parseFloat(eventFormData.latitude) : null,
        longitude: eventFormData.longitude ? parseFloat(eventFormData.longitude) : null,
        target_audience: eventFormData.target_audience,
        organizer_instagram: eventFormData.organizer_instagram || null,
        main_image: eventFormData.main_image,
        category_id: eventFormData.category_id,
        category_id_2: eventFormData.category_id_2 || null,
        commune_id: eventFormData.commune_id,
        is_permanent: eventFormData.is_permanent || false,
        operating_hours: (eventFormData.is_permanent || eventFormData.is_recurring) ? eventFormData.operating_hours : null,
        manual_override_closed: eventFormData.manual_override_closed || false,
        is_recurring: eventFormData.is_recurring || false,
        recurring_mode: eventFormData.is_recurring ? (eventFormData.recurring_mode || 'weekly') : null,
        recurring_dates: (eventFormData.is_recurring && eventFormData.recurring_mode === 'dates') ? (eventFormData.recurring_dates || []) : null,
        recurring_end_date: (eventFormData.is_recurring && (eventFormData.recurring_mode || 'weekly') === 'weekly' && eventFormData.recurring_end_date) ? eventFormData.recurring_end_date : null,
        is_online: eventFormData.is_online || false,
        // `online_url` doubles as the "Comprar entradas" CTA URL for
        // presencial events (yellow badge on EventDetail). Preserve the
        // value regardless of `is_online` so bulk-uploaded ticket links
        // (Passline/Ticketplus/etc.) survive.
        online_url: (eventFormData.online_url || '').trim() || null,
        online_platform: eventFormData.is_online ? (eventFormData.online_platform || null) : null,
        image_position: eventFormData.image_position || '50%',
        additional_images: eventFormData.additional_images || [],
        scheduled_publish_at: eventFormData.scheduled_publish_at || null,
      };

      // If reactivating an expired event, also send status=active
      if (editingEvent?._reactivating || editingEvent?.status === 'expired') {
        eventData.status = 'active';
      }

      await axios.put(`${API_URL}/api/events/${eventFormData.id}`, eventData);
      setEditDialogOpen(false);
      setEditingEvent(null);
      toast.success(editingEvent?._reactivating ? '¡Evento reactivado!' : '¡Evento actualizado!', {
        description: editingEvent?._reactivating ? 'Tu evento volvió a estar publicado.' : 'Los cambios se han guardado correctamente.'
      });
      fetchData();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Error al actualizar evento', {
        description: getErrorMessage(error)
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    showConfirmDialog(
      'Eliminar evento',
      '¿Estás seguro de eliminar este evento? Esta acción no se puede deshacer.',
      async () => {
        try {
          await axios.delete(`${API_URL}/api/events/${eventId}`);
          // In-place removal keeps pagination and filters intact — a full
          // `fetchData()` toggles `loading` which remounts the tab tree and
          // was resetting `currentPage` to 1 through the DebouncedSearchInput
          // remount side-effect.
          setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
          toast.success('Evento eliminado');
        } catch (error) {
          console.error('Failed to delete event:', error);
          toast.error('Error al eliminar evento');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const handlePlanChanged = () => {
    fetchData();
  };

  const resetEventForm = () => {
    setNewEvent({
      name: '',
      short_description: '',
      description: '',
      start_date: '',
      end_date: '',
      schedule: '',
      price: null,
      price_is_from: false,
      is_free: false,
      address: '',
      latitude: '',
      longitude: '',
      target_audience: 'family',
      organizer_instagram: '',
      main_image: '',
      category_id: '',
      category_id_2: '',
      commune_id: '',
      is_permanent: false,
      operating_hours: null,
      scheduled_publish_at: '',
      manual_override_closed: false,
      is_recurring: false,
      recurring_mode: 'weekly',
      recurring_dates: [],
      recurring_end_date: '',
      is_online: false,
      online_url: '',
      online_platform: '',
      image_position: '50%',
      additional_images: [],
      is_tour: false,
      tour_stops: [],
      // Preserve the admin's default provider — resetting the form after
      // publishing shouldn't force them to re-pick PlanCity every time.
      publish_as_provider_id: isAdmin ? (defaultAdminProviderId || '') : ''
    });
  };

  // Bulk upload handlers (admin only).
  // - handleBulkFileUpload: POST the xlsx to /api/events/bulk-parse. Drafts
  //   returned autoload the first row into the form so the admin sees the
  //   fields populated; remaining rows sit in a list to be loaded one by one.
  // - handleLoadDraft: overwrite the form with a specific draft's payload
  //   while preserving the admin's "publish as" pick.
  // - handleDismissDraft: drop a draft from the queue without publishing.
  const handleBulkFileUpload = async (file) => {
    if (!file) return;
    setBulkParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/events/bulk-parse`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const drafts = res.data?.drafts || [];
      const errors = res.data?.errors || [];
      setBulkDrafts(drafts);
      setBulkErrors(errors);
      if (drafts.length > 0) {
        applyDraftToForm(drafts[0]);
        toast.success(`Se cargaron ${drafts.length} evento${drafts.length === 1 ? '' : 's'} desde el Excel.`, {
          description: errors.length > 0 ? `${errors.length} fila(s) con error se omitieron.` : undefined,
        });
      } else if (errors.length > 0) {
        toast.error('No pudimos leer eventos del Excel.', { description: `${errors.length} fila(s) con error.` });
      } else {
        toast.error('El Excel no contiene eventos.');
      }
    } catch (err) {
      console.error('bulk-parse failed:', err);
      toast.error('Error al procesar Excel', { description: getErrorMessage(err) });
    } finally {
      setBulkParsing(false);
    }
  };

  const applyDraftToForm = (draft) => {
    if (!draft?.payload) return;
    const p = draft.payload;
    setNewEvent(prev => ({
      ...prev,
      ...p,
      // Format dates for <input type="datetime-local"> (YYYY-MM-DDTHH:MM).
      start_date: p.start_date ? String(p.start_date).slice(0, 16) : '',
      end_date: p.end_date ? String(p.end_date).slice(0, 16) : '',
      // Preserve the admin's "publish as" pick from the current form state.
      publish_as_provider_id: prev.publish_as_provider_id || (isAdmin ? defaultAdminProviderId : ''),
      // Force the loaded row id so we can remove it from the queue on success.
      __bulk_row: draft.row,
    }));
  };

  const handleDownloadBulkTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events/bulk-template`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla-eventos-plancity.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No pudimos descargar la plantilla.', { description: getErrorMessage(err) });
    }
  };

  const handleDismissDraft = (row) => {
    setBulkDrafts(prev => prev.filter(d => d.row !== row));
  };

  // "Publicar todos": POST every draft in the queue one at a time so we
  // reuse the existing /api/events endpoint (plan limits, duplicate check,
  // Telegram notification, auto-approve, provider assignment via
  // publish_as_provider_id all keep working). Failures leave the draft in
  // the queue so the admin can retry or edit them.
  const [bulkPublishingAll, setBulkPublishingAll] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { current, total }

  // Pre-flight duplicate check: before publishing the whole queue, ask the
  // backend which drafts collide with existing events. We surface a system
  // modal listing "Nuevos: X" vs "Duplicados: Y" so the admin sees exactly
  // which rows will be skipped and why (no duplicates are ever published).
  const handlePublishAllDrafts = async () => {
    if (bulkDrafts.length === 0) return;
    setBulkPreflightLoading(true);
    try {
      const items = bulkDrafts.map((d) => ({
        row: d.row,
        name: d.payload?.name,
        start_date: d.payload?.start_date,
        venue_name: d.payload?.venue_name,
        commune_id: d.payload?.commune_id,
      }));
      const res = await axios.post(`${API_URL}/api/events/bulk-check-duplicates`, { items });
      const results = res.data?.results || [];
      const dupSet = new Set(results.filter((r) => r.is_duplicate).map((r) => String(r.row)));
      const duplicates = bulkDrafts
        .filter((d) => dupSet.has(String(d.row)))
        .map((d) => {
          const match = results.find((r) => String(r.row) === String(d.row));
          return { row: d.row, name: d.payload?.name, existing: match?.existing || null };
        });
      const newDrafts = bulkDrafts.filter((d) => !dupSet.has(String(d.row)));
      setBulkDuplicateReport({
        newDrafts,
        duplicates,
        total: bulkDrafts.length,
      });
    } catch (err) {
      toast.error('No se pudo verificar duplicados', { description: getErrorMessage(err) });
    } finally {
      setBulkPreflightLoading(false);
    }
  };

  // Actually publish only the non-duplicate drafts. Called from the report
  // modal's "Publicar N nuevos" button. Duplicates never touch /api/events.
  const confirmPublishNewOnly = async () => {
    const report = bulkDuplicateReport;
    if (!report) return;
    const drafts = report.newDrafts;
    setBulkDuplicateReport(null);
    if (drafts.length === 0) {
      // Nothing new — just drop the queue and close.
      const dupRows = new Set(report.duplicates.map((d) => d.row));
      setBulkDrafts((prev) => prev.filter((d) => !dupRows.has(d.row)));
      if (report.duplicates.length > 0) {
        toast.info('Sin novedades', {
          description: `Los ${report.duplicates.length} evento${report.duplicates.length === 1 ? '' : 's'} del archivo ya existían.`,
        });
      }
      // Close the create modal if nothing else remains.
      if (bulkDrafts.length === report.duplicates.length) {
        setCreateDialogOpen(false);
        resetEventForm();
      }
      return;
    }
    setBulkPublishingAll(true);
    setBulkProgress({ current: 0, total: drafts.length });
    const results = { ok: 0, failed: [] };
    const publishAsId = newEvent.publish_as_provider_id || (isAdmin ? defaultAdminProviderId : '');
    for (let i = 0; i < drafts.length; i++) {
      const d = drafts[i];
      setBulkProgress({ current: i + 1, total: drafts.length });
      try {
        const payload = { ...d.payload };
        if (publishAsId) payload.publish_as_provider_id = publishAsId;
        await axios.post(`${API_URL}/api/events`, payload);
        results.ok += 1;
      } catch (err) {
        results.failed.push({ row: d.row, name: d.payload?.name, message: getErrorMessage(err) });
      }
    }
    setBulkPublishingAll(false);
    setBulkProgress(null);
    // Drop everything that published or was skipped as duplicate; keep only failed rows.
    const failedRows = new Set(results.failed.map((f) => f.row));
    const dupRows = new Set(report.duplicates.map((d) => d.row));
    setBulkDrafts((prev) => prev.filter((d) => failedRows.has(d.row) && !dupRows.has(d.row)));
    if (results.failed.length > 0) {
      setBulkErrors((prev) => [
        ...prev.filter((e) => !failedRows.has(e.row)),
        ...results.failed.map((f) => ({ row: f.row, message: `${f.name || 'Fila ' + f.row}: ${f.message}` })),
      ]);
    }
    if (results.failed.length === 0) {
      setCreateDialogOpen(false);
      resetEventForm();
    }
    fetchData();
    const parts = [`${results.ok} publicado${results.ok === 1 ? '' : 's'}`];
    if (report.duplicates.length) parts.push(`${report.duplicates.length} duplicado${report.duplicates.length === 1 ? '' : 's'} omitido${report.duplicates.length === 1 ? '' : 's'}`);
    if (results.failed.length) parts.push(`${results.failed.length} con error`);
    if (results.ok > 0) {
      toast.success('Carga masiva completada', { description: parts.join(' · ') });
    } else if (results.failed.length > 0) {
      toast.error('Carga masiva con errores', { description: parts.join(' · ') });
    } else {
      toast.warning('Nada para publicar', { description: parts.join(' · ') });
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent({
      ...event,
      start_date: (event.is_permanent || event.is_recurring) ? '' : (event.start_date ? event.start_date.slice(0, 16) : ''),
      end_date: (event.is_permanent || event.is_recurring) ? '' : (event.end_date ? event.end_date.slice(0, 16) : ''),
      is_permanent: event.is_permanent || false,
      operating_hours: event.operating_hours || null,
      manual_override_closed: event.manual_override_closed || false,
      is_recurring: event.is_recurring || false,
      recurring_mode: event.recurring_mode || 'weekly',
      recurring_dates: event.recurring_dates || [],
      recurring_end_date: event.recurring_end_date ? String(event.recurring_end_date).slice(0, 10) : '',
      is_online: event.is_online || false,
      online_url: event.online_url || '',
      online_platform: event.online_platform || '',
      image_position: event.image_position || '50%',
      additional_images: event.additional_images || [],
      organizer_instagram: event.organizer_instagram || '',
      // Convert stored ISO ("2026-07-27T21:50:00-04:00") to the datetime-local format
      // ("2026-07-27T21:50") that <input type="datetime-local"> expects.
      scheduled_publish_at: event.scheduled_publish_at ? String(event.scheduled_publish_at).slice(0, 16) : '',
    });
    setEditDialogOpen(true);
  };

  const getStatusBadge = (status, event) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      scheduled: 'bg-amber-100 text-amber-800',
      pending: 'bg-yellow-100 text-yellow-700',
      draft: 'bg-gray-100 text-gray-700',
      expired: 'bg-red-100 text-red-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      active: 'Activo',
      scheduled: 'Programado',
      pending: 'Pendiente',
      draft: 'Borrador',
      expired: 'Expirado',
      rejected: 'Rechazado'
    };
    let label = labels[status] || status;
    if (status === 'scheduled' && event?.scheduled_publish_at) {
      try {
        const d = new Date(event.scheduled_publish_at);
        label = `Programado · publica el ${d.toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}`;
      } catch { /* keep default */ }
    }
    return (
      <Badge className={`${styles[status] || styles.draft} border-0 rounded-full`}>
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAF9] p-4 md:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
          </div>
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  // Apply search + status filter to events list (used in "Mis Eventos" tab).
  // Uses deferred search so the input stays responsive while large lists re-filter.
  const _q = searchQuery.trim().toLowerCase();
  const _matchesDate = (ev, isoDate) => {
    if (!isoDate) return true;
    // Permanent events: always match (they're "open" every day in some operating hours sense)
    if (ev.is_permanent) return true;
    // Recurring with explicit dates
    if (ev.is_recurring && ev.recurring_mode === 'dates') {
      const dates = ev.recurring_dates || [];
      return dates.some((d) => (d && (d.date || d)) === isoDate);
    }
    // Recurring weekly (no dates list): match if isoDate is between start_date and recurring_end_date
    if (ev.is_recurring) {
      const start = (ev.start_date || '').slice(0, 10);
      const end = (ev.recurring_end_date || ev.end_date || '').slice(0, 10);
      if (!start) return true;
      if (end && isoDate > end) return false;
      return isoDate >= start;
    }
    // Regular event: match if isoDate is within [start_date, end_date]
    const start = (ev.start_date || '').slice(0, 10);
    const end = (ev.end_date || ev.start_date || '').slice(0, 10);
    if (!start) return false;
    if (isoDate < start) return false;
    if (end && isoDate > end) return false;
    return true;
  };
  const filteredEvents = events.filter((ev) => {
    if (statusFilter === 'active' && ev.status !== 'active') return false;
    if (statusFilter === 'inactive' && ev.status === 'active') return false;
    if (statusFilter === 'featured' && !(ev.is_featured && ev.featured_type !== 'carousel')) return false;
    if (statusFilter === 'carousel' && !(ev.is_featured && ev.featured_type === 'carousel')) return false;
    if (typeFilter === 'recurring' && !ev.is_recurring) return false;
    if (typeFilter === 'permanent' && !ev.is_permanent) return false;
    if (typeFilter === 'online' && !ev.is_online) return false;
    if (typeFilter === 'regular' && (ev.is_recurring || ev.is_permanent || ev.is_online)) return false;
    if (regionFilter !== 'all' && ev.region_id !== regionFilter) return false;
    if (communeFilter !== 'all' && ev.commune_id !== communeFilter) return false;
    if (providerFilter !== 'all' && ev.provider_id !== providerFilter) return false;
    if (!_matchesDate(ev, dateFilter)) return false;
    if (!_q) return true;
    const hay = `${ev.name || ''} ${ev.commune_name || ''} ${ev.category_name || ''}`.toLowerCase();
    return hay.includes(_q);
  });

  // Regions & communes derived from the provider's own events (so the selects
  // only show places the provider has actually published in). Deduped by id.
  // Plain computation (not useMemo) because it must run before the early loading return.
  const providerRegions = (() => {
    const map = new Map();
    for (const ev of events) {
      if (ev.region_id && ev.region_name && !map.has(ev.region_id)) {
        map.set(ev.region_id, { id: ev.region_id, name: ev.region_name });
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  })();
  const providerCommunes = (() => {
    const map = new Map();
    for (const ev of events) {
      if (!ev.commune_id || !ev.commune_name) continue;
      if (regionFilter !== 'all' && ev.region_id !== regionFilter) continue;
      if (!map.has(ev.commune_id)) {
        map.set(ev.commune_id, { id: ev.commune_id, name: ev.commune_name });
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  })();

  return (
    <div className="min-h-screen bg-[#F8FAF9]" data-testid="provider-dashboard">
      {/* Header */}
      <div className="bg-[#064E3B] text-white py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Hola, {user?.provider?.business_name || user?.name}
            </h1>
            <p className="text-green-100">
              {isAdmin ? 'Gestiona todos los eventos de la plataforma' : 'Gestiona tus eventos y métricas'}
            </p>
          </div>
          {membership && (
            <Badge className="mt-4 md:mt-0 w-fit bg-yellow-500/20 text-yellow-100 border-0">
              <Crown className="w-4 h-4 mr-1" />
              Plan {membership.plan_name}
            </Badge>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Eventos</p>
                  <p className="text-2xl font-bold text-[#064E3B]">{stats?.total_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Activos</p>
                  <p className="text-2xl font-bold text-[#064E3B]">{stats?.active_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm" data-testid="provider-stats-views">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Eye className="w-6 h-6 text-[#00CC66]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vistas totales</p>
                  <p className="text-2xl font-bold text-[#064E3B]">
                    {((stats?.total_views ?? events.reduce((s, e) => s + (e.views || 0), 0)) || 0).toLocaleString('es-CL')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-full p-1 mb-6 flex-wrap h-auto">
            <TabsTrigger value="onboarding" className="rounded-full" data-testid="provider-tab-onboarding">
              <Rocket className="w-4 h-4 mr-2" />
              Inicio
            </TabsTrigger>
            <TabsTrigger value="events" className="rounded-full">
              <Calendar className="w-4 h-4 mr-2" />
              {isAdmin ? 'Todos los eventos' : 'Mis Eventos'}
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-full relative">
              <Inbox className="w-4 h-4 mr-2" />
              Mensajes
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full">
              <User className="w-4 h-4 mr-2" />
              Mi Perfil
            </TabsTrigger>
            <TabsTrigger value="membership" className="rounded-full">
              <CreditCard className="w-4 h-4 mr-2" />
              Membresía
            </TabsTrigger>
          </TabsList>

          {/* Onboarding Tab */}
          <TabsContent value="onboarding">
            <OnboardingTab
              provider={{
                business_name: user?.provider?.business_name,
                logo_url: user?.provider?.logo_url,
                description: user?.provider?.description,
                phone: user?.provider?.phone,
                instagram: user?.provider?.instagram,
                events_count: stats?.total_events || 0,
                trial_ends_at: user?.provider?.trial_ends_at,
                trial_plan: user?.provider?.trial_plan,
                trial_days_remaining: user?.provider?.trial_days_remaining,
              }}
              onGoToTab={(tab) => {
                setActiveTab(tab);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Inbox className="w-5 h-5" />
                  Bandeja de Entrada
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white rounded-full">{unreadCount} nuevos</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No tienes mensajes aún</p>
                    <p className="text-sm text-gray-400">Los mensajes de contacto de tus eventos aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-xl border ${msg.is_read ? 'bg-white border-gray-100' : 'bg-green-50 border-green-200'}`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-[#064E3B]">{msg.sender_name}</span>
                              {!msg.is_read && (
                                <Badge className="bg-green-500 text-white text-xs rounded-full">Nuevo</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              Evento: <span className="font-medium">{msg.event_name}</span>
                            </p>
                            <p className="text-gray-700 mb-3">{msg.message}</p>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                              <a href={`tel:${msg.sender_phone}`} className="flex items-center gap-1 hover:text-[#00CC66]">
                                <Phone className="w-4 h-4" />
                                {msg.sender_phone}
                              </a>
                              <a href={`mailto:${msg.sender_email}`} className="flex items-center gap-1 hover:text-[#00CC66]">
                                <Mail className="w-4 h-4" />
                                {msg.sender_email}
                              </a>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                              {msg.created_at && formatChile(msg.created_at, "d 'de' MMMM yyyy 'a las' HH:mm")}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            {!msg.is_read && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-full text-xs"
                                onClick={() => handleMarkAsRead(msg.id)}
                              >
                                <CheckCheck className="w-3 h-3 mr-1" />
                                Marcar leído
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-xs"
                              onClick={() => navigate(`/event/${msg.event_id}`)}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Ver evento
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-xs text-red-500 hover:bg-red-50"
                              onClick={() => handleDeleteMessage(msg.id)}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Photo Upload Card */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Foto de Perfil</CardTitle>
                  <CardDescription>Esta foto se mostrará en tu perfil público</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <div className="relative mb-4">
                    <div className="w-40 h-40 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center border-4 border-white shadow-lg">
                      {profile.logo_url ? (
                        <img 
                          src={resolveImageUrl(profile.logo_url)} 
                          alt="Foto de perfil" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-16 h-16 text-gray-300" />
                      )}
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="absolute bottom-0 right-0 p-3 bg-[#00CC66] text-white rounded-full shadow-lg hover:bg-[#00B359] transition-colors"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Camera className="w-5 h-5" />
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-sm text-gray-500 text-center">
                    Haz clic en el ícono de cámara para cambiar tu foto
                  </p>
                </CardContent>
              </Card>

              {/* Profile Info Card */}
              <Card className="rounded-2xl border-0 shadow-sm lg:col-span-2">
                <CardHeader>
                  <CardTitle>Información del Perfil</CardTitle>
                  <CardDescription>Esta información se mostrará públicamente</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre del negocio / Artista *</Label>
                      <Input
                        value={profile.business_name}
                        onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                        placeholder="Ej: Festival de Música Chile"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={profile.description}
                        onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                        placeholder="Cuéntanos sobre ti o tu negocio..."
                        rows={4}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Teléfono
                        </Label>
                        <Input
                          type="tel"
                          value={profile.phone}
                          onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                          placeholder="+56 9 1234 5678"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          Sitio web
                        </Label>
                        <Input
                          type="text"
                          value={profile.website}
                          onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                          placeholder="www.ejemplo.com"
                        />
                        <p className="text-xs text-gray-400">Ej: www.miempresa.cl o https://miempresa.cl</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                          Instagram
                        </Label>
                        <Input
                          type="text"
                          value={profile.instagram}
                          onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                          placeholder="@miempresa"
                        />
                        <p className="text-xs text-gray-400">Ej: @pubelrincon o https://instagram.com/pubelrincon</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end gap-4 pt-4">
                      {saveSuccess && (
                        <div className="flex items-center gap-2 text-green-600 animate-in fade-in">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">¡Cambios guardados!</span>
                        </div>
                      )}
                      <Button type="submit" className="btn-primary" disabled={submitting}>
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          'Guardar cambios'
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {/* Plan Limits Indicator (chips also act as quick filters — click again to toggle off) */}
            {planLimits && (
              <div className="flex flex-wrap gap-4 mb-4" data-testid="plan-limits">
                <button
                  type="button"
                  onClick={() => { setStatusFilter(statusFilter === 'active' ? 'all' : 'active'); setCurrentPage(1); document.querySelector('[data-testid="provider-events-toolbar"]')?.scrollIntoView({behavior:'smooth',block:'nearest'}); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] ${
                    statusFilter === 'active' ? 'ring-2 ring-offset-1 ring-emerald-500 ' : ''
                  }${planLimits.can_create_event ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                  data-testid="filter-chip-active"
                  title={statusFilter === 'active' ? 'Desmarcar para ver todos (incluye no activos)' : 'Ver sólo eventos activos'}
                >
                  <Calendar className="w-4 h-4" />
                  Eventos activos: {planLimits.active_events}/{planLimits.max_events === -1 ? '∞' : planLimits.max_events}
                </button>
                <button
                  type="button"
                  onClick={() => { setStatusFilter(statusFilter === 'featured' ? 'all' : 'featured'); setCurrentPage(1); document.querySelector('[data-testid="provider-events-toolbar"]')?.scrollIntoView({behavior:'smooth',block:'nearest'}); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] ${
                    statusFilter === 'featured' ? 'ring-2 ring-offset-1 ring-amber-500 ' : ''
                  }${planLimits.max_featured_events === 0 ? 'bg-gray-50 text-gray-400' :
                    planLimits.can_feature_event ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                  data-testid="filter-chip-featured"
                  title={statusFilter === 'featured' ? 'Desmarcar para ver todos' : 'Ver sólo eventos destacados'}
                >
                  <Star className="w-4 h-4" />
                  Destacados: {planLimits.featured_events}/{planLimits.max_featured_events === -1 ? '∞' : planLimits.max_featured_events}
                  {planLimits.bonus_featured_slots > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold" title="Slots extra otorgados por el administrador">
                      +{planLimits.bonus_featured_slots}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setStatusFilter(statusFilter === 'carousel' ? 'all' : 'carousel'); setCurrentPage(1); document.querySelector('[data-testid="provider-events-toolbar"]')?.scrollIntoView({behavior:'smooth',block:'nearest'}); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] ${
                    statusFilter === 'carousel' ? 'ring-2 ring-offset-1 ring-purple-500 ' : ''
                  }${planLimits.max_carousel_events === 0 ? 'bg-gray-50 text-gray-400' :
                    planLimits.carousel_events < planLimits.max_carousel_events ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                  data-testid="filter-chip-carousel"
                  title={statusFilter === 'carousel' ? 'Desmarcar para ver todos' : 'Ver sólo eventos imperdibles'}
                >
                  <CreditCard className="w-4 h-4" />
                  Imperdibles: {planLimits.carousel_events}/{planLimits.max_carousel_events === -1 ? '∞' : planLimits.max_carousel_events}
                  {planLimits.bonus_carousel_slots > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold" title="Slots extra otorgados por el administrador">
                      +{planLimits.bonus_carousel_slots}
                    </span>
                  )}
                </button>
              </div>
            )}

            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <CardTitle className="shrink-0">{t('myEvents')}</CardTitle>
                <div className="flex flex-1 md:justify-end items-center gap-3 min-w-0">
                  <div className="flex-1 min-w-0 md:max-w-md">
                    <DebouncedSearchInput
                      initialValue={searchQuery}
                      placeholder="Buscar evento por nombre, comuna o categoría..."
                      onChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
                      className="rounded-xl bg-white w-full"
                      data-testid="provider-events-search"
                    />
                  </div>
                  <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  {isAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleExportEvents}
                      disabled={exportingEvents}
                      className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      data-testid="export-events-btn"
                      title="Descarga todos los eventos como .xlsx"
                    >
                      {exportingEvents ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exportando…</>
                      ) : (
                        <><FileSpreadsheet className="w-4 h-4 mr-2" />Exportar Excel</>
                      )}
                    </Button>
                  )}
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary" 
                      data-testid="create-event-btn"
                      disabled={planLimits && !planLimits.can_create_event}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {planLimits && !planLimits.can_create_event ? 'Límite alcanzado' : t('createEvent')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] top-[1rem] translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Crear nuevo evento</DialogTitle>
                    </DialogHeader>
                    <EventForm 
                      event={newEvent} 
                      setEvent={setNewEvent} 
                      onSubmit={handleCreateEvent}
                      isEdit={false}
                      categories={categories}
                      communes={communes}
                      regions={regions}
                      venues={venues}
                      submitting={submitting}
                      onCancel={() => setCreateDialogOpen(false)}
                      t={t}
                      isAdmin={isAdmin}
                      adminProviders={adminProviders}
                      bulkDrafts={bulkDrafts}
                      bulkErrors={bulkErrors}
                      onBulkFile={handleBulkFileUpload}
                      onDownloadTemplate={handleDownloadBulkTemplate}
                      onLoadDraft={applyDraftToForm}
                      onDismissDraft={handleDismissDraft}
                      onPublishAllDrafts={handlePublishAllDrafts}
                      bulkParsing={bulkParsing}
                      bulkPublishingAll={bulkPublishingAll}
                      bulkPreflightLoading={bulkPreflightLoading}
                      bulkProgress={bulkProgress}
                    />
                  </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No tienes eventos aún</p>
                    <p className="text-sm text-gray-400">Crea tu primer evento para comenzar</p>
                  </div>
                ) : (
                  <>
                    {/* Filter toolbar (search moved into CardHeader) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="provider-events-toolbar">
                      <div className="relative w-full">
                        <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <Input
                          type="date"
                          value={dateFilter}
                          onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                          className="pl-9 pr-8 rounded-xl bg-white"
                          data-testid="provider-events-date-filter"
                          title="Filtrar eventos por fecha"
                        />
                        {dateFilter && (
                          <button
                            type="button"
                            onClick={() => { setDateFilter(''); setCurrentPage(1); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 w-5 h-5 flex items-center justify-center rounded-full"
                            title="Limpiar fecha"
                            data-testid="provider-events-date-clear"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <Select
                        value={regionFilter}
                        onValueChange={(v) => { setRegionFilter(v); setCommuneFilter('all'); setCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-full rounded-xl bg-white" data-testid="provider-events-region-filter">
                          <SelectValue placeholder="Región" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas las regiones</SelectItem>
                          {providerRegions.map((r) => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={communeFilter}
                        onValueChange={(v) => { setCommuneFilter(v); setCurrentPage(1); }}
                        disabled={providerCommunes.length === 0}
                      >
                        <SelectTrigger className="w-full rounded-xl bg-white" data-testid="provider-events-commune-filter">
                          <SelectValue placeholder="Comuna" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas las comunas</SelectItem>
                          {providerCommunes.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={typeFilter}
                        onValueChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-full rounded-xl bg-white" data-testid="provider-events-type-filter">
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los tipos</SelectItem>
                          <SelectItem value="regular">Puntuales</SelectItem>
                          <SelectItem value="recurring">Recurrentes</SelectItem>
                          <SelectItem value="permanent">Permanentes</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={statusFilter}
                        onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-full rounded-xl bg-white" data-testid="provider-events-status-filter">
                          <SelectValue placeholder="Estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los estados</SelectItem>
                          <SelectItem value="active">Solo activos</SelectItem>
                          <SelectItem value="inactive">No activos</SelectItem>
                          <SelectItem value="featured">Destacados</SelectItem>
                          <SelectItem value="carousel">Imperdibles</SelectItem>
                        </SelectContent>
                      </Select>
                      {isAdmin && adminProviders.length > 0 && (
                        <Select
                          value={providerFilter}
                          onValueChange={(v) => { setProviderFilter(v); setCurrentPage(1); }}
                        >
                          <SelectTrigger className="w-full rounded-xl bg-white" data-testid="provider-events-provider-filter">
                            <SelectValue placeholder="Proveedor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los proveedores</SelectItem>
                            {adminProviders.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.business_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {filteredEvents.length === 0 ? (
                      <div className="text-center py-12" data-testid="provider-events-empty-filter">
                        <Search className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No se encontraron eventos con esos filtros</p>
                      </div>
                    ) : (
                    <div className="space-y-4">
                      {filteredEvents
                        .slice((currentPage - 1) * eventsPerPage, currentPage * eventsPerPage)
                        .map((event) => (
                        <div
                          key={event.id}
                          className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 p-3 md:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                          data-testid={`provider-event-${event.id}`}
                        >
                          <div className="flex items-start gap-3 md:gap-4 min-w-0">
                            <EventThumb src={event.main_image} alt={event.name} eventId={event.id} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-[#064E3B] truncate min-w-0">
                                  <a
                                    href={`/event/${event.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-[#00CC66] hover:underline transition-colors"
                                    data-testid={`provider-event-link-${event.id}`}
                                  >
                                    {event.name}
                                  </a>
                                </h3>
                                {event.is_permanent && (
                                  <Badge className="bg-blue-100 text-blue-700 border-0 rounded-full text-xs shrink-0" data-testid={`permanent-badge-${event.id}`}>
                                    <Building2 className="w-3 h-3 mr-1" />
                                    Permanente
                                  </Badge>
                                )}
                                {event.is_recurring && (
                                  <Badge className="bg-orange-100 text-orange-700 border-0 rounded-full text-xs shrink-0" data-testid={`recurring-badge-${event.id}`}>
                                    <Clock className="w-3 h-3 mr-1" />
                                    Recurrente
                                  </Badge>
                                )}
                                {event.is_online && (
                                  <Badge className="bg-violet-100 text-violet-700 border-0 rounded-full text-xs shrink-0" data-testid={`online-badge-${event.id}`}>
                                    <Video className="w-3 h-3 mr-1" />
                                    Online
                                  </Badge>
                                )}
                                {event.is_permanent && event.manual_override_closed && (
                                  <Badge className="bg-red-100 text-red-700 border-0 rounded-full text-xs shrink-0" data-testid={`closed-badge-${event.id}`}>
                                    Cerrado
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs md:text-sm text-gray-500 mt-1">
                                {event.is_permanent ? 'Evento permanente' : event.is_recurring ? 'Evento recurrente' : (event.start_date && formatChile(event.start_date, "d MMM yyyy"))}
                                {event.commune_name && ` • ${event.commune_name}`}
                                {event.category_name && ` • ${event.category_name}`}
                                {isAdmin && event.provider_name && ` • ${event.provider_name}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:gap-3 flex-nowrap md:ml-auto overflow-x-auto md:overflow-visible">
                            {event.discounts_enabled && (
                              <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full text-xs whitespace-nowrap shrink-0">
                                <Ticket className="w-3 h-3 mr-1" />
                                Descuento
                              </Badge>
                            )}
                            {event.is_featured && event.featured_type !== 'carousel' && (
                              <Badge className="bg-orange-100 text-orange-700 border-0 rounded-full text-xs whitespace-nowrap shrink-0" data-testid={`badge-featured-${event.id}`}>
                                <Star className="w-3 h-3 mr-1 fill-current" />
                                Destacado
                              </Badge>
                            )}
                            {event.is_featured && event.featured_type === 'carousel' && (
                              <Badge className="bg-purple-100 text-purple-700 border-0 rounded-full text-xs whitespace-nowrap shrink-0" data-testid={`badge-carousel-${event.id}`}>
                                <GalleryHorizontal className="w-3 h-3 mr-1" />
                                Imperdible
                              </Badge>
                            )}
                            {/* Geolocation badge: green when the event has both
                                lat/lng (visible in the map), gray with a subtle
                                slash-through cue when missing so admins can spot
                                events that need geocoding. */}
                            {(event.latitude != null && event.longitude != null) ? (
                              <Badge
                                className="bg-green-100 text-green-700 border-0 rounded-full text-xs whitespace-nowrap shrink-0"
                                title="En el mapa"
                                data-testid={`badge-geo-${event.id}`}
                              >
                                <MapPin className="w-3 h-3 mr-1" />
                                En mapa
                              </Badge>
                            ) : (
                              <Badge
                                className="bg-gray-100 text-gray-500 border-0 rounded-full text-xs whitespace-nowrap shrink-0"
                                title="Sin coordenadas — no aparece en el mapa"
                                data-testid={`badge-nogeo-${event.id}`}
                              >
                                <MapPin className="w-3 h-3 mr-1 opacity-60" />
                                Sin coord.
                              </Badge>
                            )}
                            {getStatusBadge(event.status, event)}
                            <div
                              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B]"
                              data-testid={`provider-event-views-${event.id}`}
                              title="Vistas del evento"
                            >
                              <Eye className="w-4 h-4 text-[#00CC66]" />
                              {(event.views || 0).toLocaleString('es-CL')}
                            </div>
                            <div className="flex gap-1 flex-nowrap">
                              {event.is_permanent && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className={`rounded-full ${event.manual_override_closed ? 'text-red-500 hover:text-red-600' : 'text-green-500 hover:text-green-600'}`}
                                  onClick={() => handleToggleClosed(event.id)}
                                  title={event.manual_override_closed ? 'Reabrir local' : 'Cerrar temporalmente'}
                                  data-testid={`toggle-closed-${event.id}`}
                                >
                                  {event.manual_override_closed ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
                                </Button>
                              )}
                              {(isAdmin || (planLimits && planLimits.max_featured_events !== 0)) && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className={`rounded-full ${event.is_featured && event.featured_type !== 'carousel' ? 'text-orange-500 hover:text-orange-600' : 'text-gray-400 hover:text-orange-500'}`}
                                  onClick={() => handleToggleFeature(event, 'homepage')}
                                  title={event.is_featured && event.featured_type !== 'carousel' ? 'Quitar de destacados' : 'Marcar como destacado'}
                                  data-testid={`toggle-featured-${event.id}`}
                                >
                                  <Star className={`w-4 h-4 ${event.is_featured && event.featured_type !== 'carousel' ? 'fill-current' : ''}`} />
                                </Button>
                              )}
                              {(isAdmin || (planLimits && planLimits.max_carousel_events > 0)) && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className={`rounded-full ${event.is_featured && event.featured_type === 'carousel' ? 'text-purple-500 hover:text-purple-600' : 'text-gray-400 hover:text-purple-500'}`}
                                  onClick={() => handleToggleFeature(event, 'carousel')}
                                  title={event.is_featured && event.featured_type === 'carousel' ? 'Quitar de imperdibles' : 'Marcar como imperdible'}
                                  data-testid={`toggle-carousel-${event.id}`}
                                >
                                  <GalleryHorizontal className="w-4 h-4" />
                                </Button>
                              )}
                              {planLimits && planLimits.max_featured_events !== 0 && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className={`rounded-full ${event.discounts_enabled ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-amber-500'}`}
                                  onClick={() => handleToggleDiscounts(event.id)}
                                  title={event.discounts_enabled ? 'Desactivar descuentos' : 'Activar descuentos'}
                                >
                                  <Ticket className="w-4 h-4" />
                                </Button>
                              )}
                              {event.discounts_enabled && membership?.plan === 'premium' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full h-8 px-3 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                                  onClick={() => setTicketsEvent(event)}
                                  title="Ver y marcar tickets"
                                  data-testid={`tickets-btn-${event.id}`}
                                >
                                  <Ticket className="w-3.5 h-3.5 mr-1" /> Tickets
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                className="rounded-full"
                                onClick={() => navigate(`/event/${event.id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="rounded-full"
                                onClick={() => openEditDialog(event)}
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {event.status === 'expired' && (
                                <Button
                                  size="sm"
                                  className="rounded-full h-8 px-3 text-xs bg-[#00CC66] hover:bg-[#00b359] text-white"
                                  onClick={() => openEditDialog({ ...event, _reactivating: true })}
                                  title="Reactivar evento con nueva fecha"
                                  data-testid={`reactivate-event-${event.id}`}
                                >
                                  <Rocket className="w-3.5 h-3.5 mr-1" /> Reactivar
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                className="rounded-full text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                                onClick={() => handleDuplicateEvent(event)}
                                title="Duplicar evento"
                                data-testid={`duplicate-event-${event.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteEvent(event.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                    
                    {/* Pagination */}
                    {filteredEvents.length > eventsPerPage && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <p className="text-sm text-gray-500">
                          Mostrando {((currentPage - 1) * eventsPerPage) + 1} - {Math.min(currentPage * eventsPerPage, filteredEvents.length)} de {filteredEvents.length} eventos
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Anterior
                          </Button>
                          <div className="flex items-center gap-1" data-testid="provider-events-pagination-numbers">
                            {(() => {
                              // Compact pagination: show first, last, current +/- 2 with ellipses.
                              // Handles 30+ pages without spilling across the screen.
                              const totalPages = Math.max(1, Math.ceil(filteredEvents.length / eventsPerPage));
                              const cur = currentPage;
                              const pages = new Set([1, totalPages, cur - 1, cur, cur + 1]);
                              const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
                              const out = [];
                              let prev = 0;
                              for (const p of sorted) {
                                if (p - prev > 1) out.push({ ellipsis: true, key: `e${prev}` });
                                out.push({ page: p, key: p });
                                prev = p;
                              }
                              return out.map(item => item.ellipsis ? (
                                <span key={item.key} className="px-1 text-gray-400 select-none">…</span>
                              ) : (
                                <Button
                                  key={item.key}
                                  variant={cur === item.page ? "default" : "outline"}
                                  size="sm"
                                  className={`rounded-full w-8 h-8 p-0 ${cur === item.page ? 'bg-[#00CC66] hover:bg-[#00B359]' : ''}`}
                                  onClick={() => setCurrentPage(item.page)}
                                >
                                  {item.page}
                                </Button>
                              ));
                            })()}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredEvents.length / eventsPerPage), p + 1))}
                            disabled={currentPage >= Math.ceil(filteredEvents.length / eventsPerPage)}
                          >
                            Siguiente
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Membership Tab */}
          <TabsContent value="membership">
            <MembershipTab
              membership={membership}
              plans={plans}
              onPlanChanged={handlePlanChanged}
            />
          </TabsContent>
        </Tabs>

        {/* Tickets Dialog (Premium providers) */}
        <EventTicketsDialog
          event={ticketsEvent}
          open={!!ticketsEvent}
          onClose={() => setTicketsEvent(null)}
        />

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] top-[1rem] translate-y-0 sm:top-[50%] sm:translate-y-[-50%] sm:max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent?._reactivating ? 'Reactivar evento' : 'Editar evento'}</DialogTitle>
              {editingEvent?._reactivating && (
                <p className="text-sm text-gray-600 mt-1">
                  Para reactivar este evento, <strong>ingresa una nueva fecha futura</strong> (hora de Chile).
                  El evento volverá a estar publicado y visible para todos.
                </p>
              )}
            </DialogHeader>
            {editingEvent && (
              <EventForm 
                event={editingEvent} 
                setEvent={setEditingEvent} 
                onSubmit={handleEditEvent}
                isEdit={true}
                categories={categories}
                communes={communes}
                regions={regions}
                venues={venues}
                submitting={submitting}
                onCancel={() => setEditDialogOpen(false)}
                t={t}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Generic Confirmation Dialog */}
        <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmDialog.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDialog.onConfirm}
                className={confirmDialog.variant === 'destructive' ? 'bg-red-500 hover:bg-red-600 text-white' : 'btn-primary'}
              >
                Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* System modal shown when the backend detects a duplicate on an
            individual create. Replaces window.confirm — no "publish anyway"
            option (we always block duplicates). */}
        <Dialog open={!!duplicateInfo} onOpenChange={(o) => !o && setDuplicateInfo(null)}>
          <DialogContent className="max-w-md" data-testid="duplicate-event-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Evento duplicado
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-gray-700">
                Ya existe un evento muy similar publicado. Para evitar repeticiones no se puede volver a crear.
              </p>
              {duplicateInfo?.existing && (
                <div className="rounded-lg border bg-amber-50 border-amber-200 p-3 space-y-1">
                  <div className="font-medium text-amber-900" data-testid="duplicate-existing-name">
                    “{duplicateInfo.existing.name || 'Evento existente'}”
                  </div>
                  {duplicateInfo.existing.venue_name && (
                    <div className="text-xs text-amber-800">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {duplicateInfo.existing.venue_name}
                      {duplicateInfo.existing.commune_name ? ` · ${duplicateInfo.existing.commune_name}` : ''}
                    </div>
                  )}
                  {duplicateInfo.existing.start_date && (
                    <div className="text-xs text-amber-800">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {new Date(duplicateInfo.existing.start_date).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  )}
                  {duplicateInfo.existing.status && (
                    <div className="text-[10px] uppercase tracking-wide text-amber-700">
                      Estado: {duplicateInfo.existing.status}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500">
                Si corresponde publicar una función distinta, cambia el nombre, la fecha o el recinto y vuelve a intentarlo.
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                onClick={() => setDuplicateInfo(null)}
                className="bg-[#064E3B] hover:bg-emerald-700 text-white"
                data-testid="duplicate-event-close"
              >
                Entendido
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk pre-flight report: lists which drafts are new vs duplicated
            before hitting the backend. Only the "new" list is published. */}
        <Dialog open={!!bulkDuplicateReport} onOpenChange={(o) => !o && setBulkDuplicateReport(null)}>
          <DialogContent className="max-w-2xl" data-testid="bulk-duplicate-report-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Revisión de carga masiva
              </DialogTitle>
            </DialogHeader>
            {bulkDuplicateReport && (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg border p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">{bulkDuplicateReport.total}</div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Detectados</div>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{bulkDuplicateReport.newDrafts.length}</div>
                    <div className="text-[11px] uppercase tracking-wide text-emerald-700">Nuevos</div>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                    <div className="text-2xl font-bold text-amber-700">{bulkDuplicateReport.duplicates.length}</div>
                    <div className="text-[11px] uppercase tracking-wide text-amber-700">Ya existentes</div>
                  </div>
                </div>

                {bulkDuplicateReport.duplicates.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
                      Se omitirán ({bulkDuplicateReport.duplicates.length})
                    </div>
                    <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200 divide-y divide-amber-100" data-testid="bulk-duplicates-list">
                      {bulkDuplicateReport.duplicates.map((d) => (
                        <div key={`dup-${d.row}`} className="px-3 py-2 bg-amber-50/50 text-xs">
                          <div className="font-medium text-amber-900">Fila {d.row}: “{d.name || 'Sin título'}”</div>
                          {d.existing && (
                            <div className="text-amber-800 mt-0.5">
                              Coincide con “{d.existing.name}”
                              {d.existing.venue_name ? ` · ${d.existing.venue_name}` : ''}
                              {d.existing.start_date ? ` · ${new Date(d.existing.start_date).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bulkDuplicateReport.newDrafts.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                      Se publicarán ({bulkDuplicateReport.newDrafts.length})
                    </div>
                    <div className="max-h-40 overflow-y-auto rounded-lg border border-emerald-200 divide-y divide-emerald-100" data-testid="bulk-new-list">
                      {bulkDuplicateReport.newDrafts.map((d) => (
                        <div key={`new-${d.row}`} className="px-3 py-2 bg-emerald-50/40 text-xs text-emerald-900">
                          <span className="font-medium">Fila {d.row}:</span> {d.payload?.name || 'Sin título'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bulkDuplicateReport.newDrafts.length === 0 && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 text-center">
                    Todos los eventos del archivo ya existen. No hay nada nuevo que publicar.
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setBulkDuplicateReport(null)}
                data-testid="bulk-duplicate-report-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmPublishNewOnly}
                disabled={!bulkDuplicateReport || bulkDuplicateReport.newDrafts.length === 0}
                className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
                data-testid="bulk-duplicate-report-confirm"
              >
                <Rocket className="w-3.5 h-3.5 mr-1" />
                Publicar {bulkDuplicateReport?.newDrafts?.length || 0} nuevo{(bulkDuplicateReport?.newDrafts?.length || 0) === 1 ? '' : 's'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <Footer />
    </div>
  );
};
